import type { StudyQuestionsViewport } from "@/features/studies/embed/studyEmbedBridge";
import { useEmbedMode } from "@/features/studies/embed/useEmbedMode";
import { COMPOSER_SURFACE_CLASS, COMPOSER_ROUND_ACTION_CLASS, composerFlatSurfaceClassName } from "@/components/composer/composerAppearance";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { chatT } from "@/i18n";
import { chatLanguage, studyLanguageNote } from "../utils/chatLanguage";

import {
  ASSET_FAMILY_FACTS,
  ChatMessageCollectAttachItem,
  MaterialAttachOffer,
  MaterialOffers,
  QuestionnaireChatMessage,
  QuestionnaireCollectedAssetOperation,
  QuestionnaireDraft,
  QuestionnaireDraftReadiness,
  QuestionnaireOperationEffect,
  QuestionnaireTurnAttachmentInput,
  QuestionnaireTurnCommand,
  QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT,
  StudyDesign,
  SurveyJson,
  TestedAssetBlock,
  TestedAssetType,
  FILES_PER_ASSET,
  LINK_MATERIAL_FAMILIES,
  assetFileIds,
  attachCapacityOf,
  chatLanguageDirection,
  goalRecipe,
  isAssetFamily,
  isDirectVideoUrl,
  materialOffers,
  messageLinks,
  nonKeyframeableVideoMessage,
  savesCaptureOfLink,
  openClarificationOf,
  resolveStudyBrand,
  respondentWordingFor,
  studyMaterialFamilies,
  studySpecOf,
} from "@digital-twins/shared-types";
import { ArrowLeft, ArrowRight, Loader2, Plus, Square, X } from "@digital-twins/design-system/icons";
import { toast } from "sonner";

import type { IssuedAssetFile } from "@/lib/api/client";

import type { StudyType } from "@/features/studies/creation/types";
import { reviewEntryRefusal, type ReviewCompile } from "@/features/studies/creation/reviewEntry";
import { postStudyEmbedMessage, STUDY_EMBED_SOURCE } from "@/features/studies/embed/studyEmbedBridge";
import { useAutosizeTextarea } from "@/lib/hooks/use-autosize-textarea";
import { getUserInitials } from "@/lib/utils/avatar";
import { CHAT_TEXTAREA_MAX_HEIGHT_PX } from "@/lib/utils/sync-textarea-autosize-height";
import { cn } from "@/lib/utils";
import { languageName } from "@/features/studies/questionnaire/utils/turnCommandCopy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Text } from "@/components/ui/Text";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/ui/UserAvatar";
import Link from "@/routing/Link";
import { ROUTES } from "@/routes/routes";
import {
  AttachmentTiles,
  type AttachmentTileItem,
} from "@/components/chat/AttachmentTiles";
import { useAuth } from "@/lib/config/auth-provider";
import {
  captureQuestionnaireUrlApi,
  uploadQuestionnaireAssetFileApi,
} from "../api/questionnaireDraftsApi";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { renderPdfFirstPagePreview } from "@/lib/utils/extract-pdf-text";

import {
  AssistantReplyMessage,
  assistantReplyLines,
  ReplyLines,
  ReplyReceipts,
} from "./AssistantReplyLines";
import { CollectCard } from "./CollectCard";
import { ComposerCollectIntake } from "./ComposerCollectIntake";
import { DecisionCard } from "./DecisionCard";
import { ObjectiveReceipt } from "./ObjectiveReceipt";
import {
  answerInFlightOf,
  answeredCardsOf,
  cardsByAnswerMessage,
  openCardIdOf,
} from "./answeredCards";
import { resolveDecisionOptionLabel } from "./resolveDecisionOptionLabel";
import { resolveDecisionCardAction } from "./topicGateAction";
import { resolveExploreConfirmAction } from "./exploreConfirmAction";
import {
  type BuilderBusyCause,
  type BuilderLockCause,
  QuestionnaireBuilderPanel,
  emptyCanvasBusyLine,
  lockReasonFor,
} from "./QuestionnaireBuilderPanel";
import { CreationFlowStickyActionBar } from "@/features/studies/creation/components/CreationFlowStickyActionBar";
import { useStudyCreationBeforeUnload } from "@/features/studies/creation/hooks/useStudyCreationBeforeUnload";
import { usePublishEmbedUnsavedWork } from "@/features/studies/embed/useStudyEmbedBridge";
import { useSidebarBackGuard } from "@/contexts/SidebarBackGuardContext";
import {
  carriesTestedAssets,
  diffTestedAssetsToOperations,
} from "../utils/draftOperations";
import {
  indexBlockingIssuesByQuestionId,
  shownAuthoringIssues,
} from "../utils/indexValidationIssues";
import { formatIssueCopy } from "../utils/questionnaireIssueCopy";
import {
  COMPOSER_MEDIA_ACCEPT,
  assetUploadRefusalLine,
  gateComposerMediaFiles,
  isSupportedVideoMime,
} from "../assets/assetMediaAccept";
import {
  assetKeysOf,
  attachCollectedMedia,
  attachPlacementsOf,
  attachCollectSatisfied,
  attachItemsWithFileOf,
  attachReceiptLinesOf,
  receiptSentMedia,
  carriesCollectedItems,
  collectableLinks,
  collectAssetsOperation,
  collectedItemsOf,
  openAttachItemsOf,
  receiptAssetKeysOf,
  stagedMediaRefsOf,
  uncoveredAssetsAmong,
  type CollectedAssetMediaRef,
  type LinkedCollectedMedia,
  type SentReceiptMedia,
} from "../assets/collectAssets";
import {
  mediaRefFromIssuedFile,
  mediaRefFromPdfPreview,
  mediaRefFromUpload,
  mediaRefFromVideoUrl,
} from "../assets/conceptAssets";
import {
  builderOptionLabel,
  builderOptionMark,
  chainCodesByMessage,
} from "../utils/builderCardCopy";
import {
  addAsLabel,
  addedBackToObjectiveLine,
  addToEachLabel,
  addToItemLabel,
  alreadyAttachedLine,
  alreadyHasFileLine,
  attachReceiptLine,
  describeItLine,
  leaveOutLabel,
  leftOutLine,
  materialCardQuestion,
  newAssetsCardCopy,
  pickLeftOutLine,
  typeCapLine,
  whichToAddLine,
  useFileLabel,
} from "../utils/materialCardCopy";
import { receiptItemCount } from "../utils/turnCommandCopy";
import { userMessageReceiptLines } from "../utils/userMessageReceiptLines";
import { assetPanelLabel } from "../assets/conceptAssets";
import { useQuestionnaireCoverage } from "../hooks/useQuestionnaireCoverage";
import { useQuestionnaireDraftSession } from "../hooks/useQuestionnaireDraftSession";
import type { SelectedPopulation } from "../utils/draftAudience";
import {
  type StudyObjectiveUpdate,
  type WorkspaceSaved,
  useQuestionnaireWorkspace,
  writeFailureCopy,
} from "../hooks/useQuestionnaireWorkspace";
import { resolveDraftForChatSend } from "./chatSendGuard";
import { ChatMessageCollect, ChatMessageOption } from "../types";
import { firstReplyLine, useIsNarrowBuilder } from "./narrowQuestionnaireView";

interface QuestionnaireBuilderChatProps {
  sourceThreadToken?: string | null;
  /** §0 goal id — seeds the chat's authored opening chain at creation. */
  goalId?: string | null;
  /** Flow-header chips (goal) rendered left of the actions. */
  headerSlot?: React.ReactNode;
  initialMessage?: string | null;
  language?: string;
  onEditAudience?: () => void;
  /**
   * Hydrates creation state from the loaded draft so ReviewStep prices from
   * the design its StudySpec resolved (`studySpec.resolved.design`).
   */
  onStudyDesign?: (design: StudyDesign | null) => void;
  /**
   * Enables the builder's language chip. Changing language after a draft
   * exists asks for confirmation, then runs an AI translation pass over the
   * current draft through the chat.
   */
  onLanguageChange?: (lang: string) => void;
  onSessionReady?: (sessionId: string) => void;
  onPresentation?: (presentation: "full" | "quick") => void;
  /**
   * Review's one entry (`enterReview`), handed the Review compile: its
   * survey, the draft revision it compiled, that revision's audience (what
   * Review shows and launch reads) and the backend's verdict, which the entry
   * applies. Resolves false when Review stays shut.
   */
  onSurveyReady: (compiled: ReviewCompile) => Promise<boolean>;
  resumeSessionId?: string | null;
  returnToStep?: number | null;
  onReturnToReview?: () => void;
  /**
   * The builder's "Save and continue" (`saveEditsIfConfirmed`), for a control
   * of the flow's own that leaves this draft (the "Continue draft" banner):
   * it resolves true when there was nothing to save or the save succeeded.
   * Set while the builder is mounted.
   */
  saveFirstRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  selectedPopulation: SelectedPopulation | null;
  selectedStorage: { id: string; name: string } | null;
  studyType: StudyType;
}

// Option values for the asset-confirm card. A click on one runs a local action
// (add it as a family, put it on an item, or leave it out) instead of being
// sent to the planner.
const ASSET_CONFIRM_CANCEL = "__cancel_asset__";

/** Per draft, for this tab: the line about the study's language was said. */
const STUDY_LANGUAGE_NOTE_KEY = "questionnaire-study-language-note:";

/** The asset-confirm card's "Add as <family>" option. */
function addAsOptionValue(family: TestedAssetType): string {
  return `__add_asset_${family}__`;
}

/** The asset-confirm card's option that puts it on its `index`th item. */
function attachOptionValue(index: number): string {
  return `__attach_asset_${index}__`;
}

/** The asset-confirm card's option that puts several files on as many items. */
const ATTACH_EACH = "__attach_asset_each__";

/**
 * The asset-confirm card's option that uses the `index`th of the files or
 * links given, when they are more than the items take (one each, F19).
 */
function pickGivenOptionValue(index: number): string {
  return `__pick_given_${index}__`;
}

/** The family an "Add as" option adds, or null for any other option. */
function familyOfAddAsOption(value: string): TestedAssetType | null {
  return (
    (Object.keys(ASSET_FAMILY_FACTS) as Array<TestedAssetType>).find(
      (family) => addAsOptionValue(family) === value,
    ) ?? null
  );
}

// Option values for the local "update the questions?" card. Yes sends a typed
// `use_new_assets` command (plan S4.2, L24). The other option leaves the
// questions to the user: the asset still has to be in a question before
// Review (`tested_asset_unused`), so it never offers to leave it out.
const NEW_ASSETS_CONFIRM = {
  update: "__use_new_assets__",
  addMyself: "__add_to_questions_myself__",
} as const;

/**
 * What a card the builder added itself does when it is answered, kept by the
 * card's id: each card carries its own, so a later card never changes what an
 * earlier one sends.
 * - `add_asset`: the asset-confirm card, with the files or links handed
 *   over, and the items
 *   it offers to put them on (`attachTo`: by option index for one, in order
 *   for several, every one when more were given than they take and the user
 *   picks the one to use). Its option is the one action: nothing is sent to
 *   the chat beside it.
 * - `use_new_assets`: the "update the questions?" card, with the assets it
 *   offers to bring in.
 */
type LocalCardAction =
  | {
      kind: "add_asset";
      files: Array<File>;
      attachTo: Array<MaterialAttachOffer>;
    }
  | { kind: "use_new_assets"; assetKeys: Array<string> };

/** The family registry's prose noun for an asset type (plan S3.4 row 39). */
function assetFamilyNoun(assetType: string): string {
  return isAssetFamily(assetType)
    ? ASSET_FAMILY_FACTS[assetType].nouns.singular
    : assetType;
}

// Shown when the advance is refused and the backend gave no issue text for it —
// i.e. the compile itself failed. It names the retry the user can actually
// perform, because clicking Review again always goes back to the compiler;
// "try saving again" pointed at a save that had already succeeded and would not
// have helped.

function isUnansweredDecision(
  message: QuestionnaireChatMessage,
  isWaiting: (message: QuestionnaireChatMessage) => boolean,
): message is QuestionnaireChatMessage & {
  options: NonNullable<QuestionnaireChatMessage["options"]>;
} {
  return (
    message.role === "assistant" &&
    Array.isArray(message.options) &&
    message.options.length > 0 &&
    isWaiting(message)
  );
}

function isUnansweredCollect(
  message: QuestionnaireChatMessage,
  isWaiting: (message: QuestionnaireChatMessage) => boolean,
): message is QuestionnaireChatMessage & {
  collect: NonNullable<QuestionnaireChatMessage["collect"]>;
} {
  return (
    message.role === "assistant" && Boolean(message.collect) && isWaiting(message)
  );
}

/**
 * A message the builder added itself (`local_`): a note or a card, which has
 * no clarification record, or a turn that ended short. The server's
 * transcript never has it.
 */
function isLocalMessage(message: { id: string }): boolean {
  return message.id.startsWith("local_");
}

function findLastUnansweredComposerMessage(
  transcript: ReadonlyArray<QuestionnaireChatMessage>,
  isWaiting: (message: QuestionnaireChatMessage) => boolean,
): QuestionnaireChatMessage | null {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const message = transcript[i];
    if (
      isUnansweredDecision(message, isWaiting) ||
      isUnansweredCollect(message, isWaiting)
    ) {
      return message;
    }
  }
  return null;
}

interface ChatBubbleProps {
  aiIconSrc: string;
  /** The study's asset panel heading, for a reply's not-done lines (F16). */
  assetPanelHeading: string;
  /** The opening-chain step that asked this message, from its record. */
  chainCode?: string | null;
  isFirst: boolean;
  isLast: boolean;
  message: AssistantReplyMessage & Pick<QuestionnaireChatMessage, "role">;
  userInitials: string;
  whiteLabelEnabled: boolean;
}

/** The Review button's blocked reason, for `aria-describedby`. */
const REVIEW_BLOCKED_REASON_ID = "questionnaire-review-blocked-reason-text";

type NarrowBuilderView = "questionnaire" | "chat";

interface NarrowReplyNote {
  id: string;
  text: string;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5">
      {[0, 150, 300].map((delay) => (
        <div
          key={delay}
          className="h-2 w-2 rounded-full bg-muted-foreground/60 motion-safe:animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  );
}

function ChatBubble(props: ChatBubbleProps) {
  const {
    aiIconSrc,
    assetPanelHeading,
    chainCode,
    isFirst,
    isLast,
    message,
    userInitials,
    whiteLabelEnabled,
  } = props;
  const isUser = message.role === "user";
  const displayContent = isUser
    ? message.content
    : assistantReplyLines(message, chainCode, assetPanelHeading).join("\n");

  return (
    <div
      data-testid={isUser ? "chat-message-user" : "chat-message-assistant"}
      className={cn(
        "flex gap-2",
        isUser ? "flex-row-reverse pl-[72px]" : "flex-row pr-[60px]",
        isLast ? "animate-fade-in" : "",
      )}
    >
      {!isUser ? (
        <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full bg-study-chat-ai-bubble flex items-center justify-center">
          <img
            src={aiIconSrc}
            alt="AI"
            className="h-full w-full object-contain"
            style={{ transform: "scale(1.3)" }}
          />
        </div>
      ) : (
        <UserAvatar initials={userInitials} size="sm" className="h-7 w-7" />
      )}

      <div
        className={cn("flex-1 min-w-0", isUser ? "text-right" : "text-left")}
      >
        {/* A reply whose writer wrote nothing shows its receipts alone (D2). */}
        {displayContent !== "" ? (
          <div
            dir={chatLanguageDirection(message.language)}
            className={cn(
              "inline-block rounded-2xl px-4 py-2.5 text-base leading-[1.6] text-start",
              // A long link has no break point of its own: it wraps inside the
              // bubble instead of running past it (QA4 AD N6).
              "max-w-full [overflow-wrap:anywhere]",
              isUser
                ? cn(
                    "rounded-tr-sm text-study-chat-user-fg",
                    whiteLabelEnabled
                      ? "bg-brand-accent/30"
                      : "bg-twin-chat-bubble/30",
                  )
                : "rounded-tl-sm bg-study-chat-ai-bubble text-foreground",
            )}
          >
            <ReplyLines id={message.id} text={displayContent} />
          </div>
        ) : null}
        {!isUser ? <ReplyReceipts message={message} /> : null}
      </div>
    </div>
  );
}

export { formatClipDuration } from "@/components/chat/AttachmentTiles";

/**
 * Files as `AttachmentTiles` items, each with its preview: an object URL for
 * an image or a video (CreativeMedia branches on the mime), the first page
 * for a PDF, else none (the document icon). The composer's staged files
 * read it.
 */
function useFileTiles(
  files: ReadonlyArray<File> | undefined,
): Array<AttachmentTileItem> {
  const [filePreviews, setFilePreviews] = useState<Array<string | null>>([]);

  useEffect(() => {
    if (!files || files.length === 0) {
      setFilePreviews([]);
      return;
    }
    let cancelled = false;
    const objectUrls: Array<string> = [];
    setFilePreviews(files.map(() => null));
    files.forEach((file, index) => {
      if (file.type.startsWith("image/") || isSupportedVideoMime(file.type)) {
        const objectUrl = URL.createObjectURL(file);
        objectUrls.push(objectUrl);
        setFilePreviews((cur) =>
          cur.map((p, i) => (i === index ? objectUrl : p)),
        );
      } else if (file.type === "application/pdf") {
        void renderPdfFirstPagePreview(file, 320)
          .then((url) => {
            if (!cancelled)
              setFilePreviews((cur) =>
                cur.map((p, i) => (i === index ? url : p)),
              );
          })
          .catch(() => {
            /* keep the document-icon fallback */
          });
      }
    });
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  return (files ?? []).map((file, index) => ({
    src: filePreviews[index] ?? "",
    mimeType: file.type || undefined,
    label: file.name,
  }));
}

/**
 * A user-side chat bubble showing the images/videos/PDFs the user attached
 * through a collect card, as they sent them (`receiptSentMedia`) — mirrors how
 * attachments read in a normal user message (right-aligned, avatar on the
 * right, thumbnails in the bubble). Exported for direct test coverage.
 */
export function SentAttachmentsBubble(props: {
  /** Each sent file's hosted src, its mime (a hosted https video URL carries
   *  none, so it rides along for the video predicate) and its name. */
  mediaUrls: Array<SentReceiptMedia>;
  userInitials: string;
  isLast: boolean;
}) {
  const { mediaUrls, userInitials, isLast } = props;

  if (mediaUrls.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-row-reverse gap-2 pl-[72px]",
        isLast && "animate-fade-in",
      )}
    >
      <UserAvatar initials={userInitials} size="sm" className="h-7 w-7" />
      <AttachmentTiles items={mediaUrls} />
    </div>
  );
}

export function QuestionnaireBuilderChat(props: QuestionnaireBuilderChatProps) {
  const {
    goalId,
    sourceThreadToken,
    headerSlot,
    initialMessage,
    language,
    onStudyDesign,
    onEditAudience,
    onLanguageChange,
    onSessionReady,
    onPresentation,
    onSurveyReady: onFullSurveyReady,
    resumeSessionId,
    returnToStep,
    onReturnToReview,
    saveFirstRef,
    selectedPopulation,
  } = props;

  const { t } = useTranslation("app");
  const isEmbeddedStudy = useEmbedMode();
  const [didAdvance, setDidAdvance] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  // The option picked on a card the builder added itself (a `local_` prompt).
  // Server cards are answered through their clarification records (S4.1).
  const [localCardAnswers, setLocalCardAnswers] = useState<
    Map<string, string>
  >(new Map());
  const [invitePaste, setInvitePaste] = useState(false);
  const [isOpeningWorkspace, setIsOpeningWorkspace] = useState(false);
  // What each card the builder added itself does when answered, by card id
  // (`LocalCardAction`). A pasted link or a dropped file waits on its
  // asset-confirm card, so a stray URL or image never silently becomes an
  // asset.
  const [localCardActions, setLocalCardActions] = useState<
    Map<string, LocalCardAction>
  >(new Map());
  // The assets whose "update the questions?" card waits for a chat question
  // to close (`offerQuestionUpdate` offers nothing while one is open). Not
  // kept across a reload, like the card itself.
  const [heldNewAssetKeys, setHeldNewAssetKeys] = useState<Array<string>>(
    [],
  );
  const [inputOtherExpanded, setInputOtherExpanded] = useState(false);
  const [inputOtherText, setInputOtherText] = useState("");
  // True while a chat-side write that carries a video is in flight: the Save
  // watches it, and that wait is said out loud rather than left silent.
  const [readingCreative, setReadingCreative] = useState(false);
  // Chat-side asset writes (`POST /operations`) run outside a chat turn, so `sending`
  // does not cover them. While one is in flight, panel edits race the write:
  // the save-race keeps the newer local draft, the write's block wins on the
  // server, and the two disagree until a reload. Counted, not boolean, because
  // a collect submit and a composer add can overlap.
  const [assetWritesInFlight, setAssetWritesInFlight] = useState(0);
  // The collect intake holds files or links the user has not added yet.
  const [intakeStaged, setIntakeStaged] = useState(false);
  // Files the user attached in the composer while no upload card is open
  // (B2): staged beside their words, which stay. A send takes them: with no
  // words, the material card asks what they are; with words, each is stored
  // first and rides with the words as the turn's typed input (L8), and the
  // turn's operations place it. A file the turn placed leaves; one it did not
  // goes, once the turn ends (`composerTurnEnded`), to the upload card the
  // reply opens (its intake's `initialFiles`), else to the material card.
  const [composerFiles, setComposerFiles] = useState<Array<File>>([]);
  // Each composer file's stored upload, once a send stored it (L8): a resend
  // of words handed back, or a card that then takes the file, reuses it, and
  // the turn's user message names it (`attachments[].placedOn`) by its file.
  const [composerUploads, setComposerUploads] = useState<
    ReadonlyMap<File, IssuedAssetFile>
  >(() => new Map());
  // Set when a composer turn that carried staged files ends (L25).
  const [composerTurnEnded, setComposerTurnEnded] = useState(false);
  // The collect card whose intake is writing its answer: its files upload and
  // its assets save before the receipt turn goes out (plan S4.1).
  const [intakeAnsweringCardId, setIntakeAnsweringCardId] = useState<
    string | null
  >(null);

  // Language picked while a draft already exists — held until the user
  // confirms the translation pass in the dialog below.
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  // The language the dialog names, kept through its closing animation: the
  // pick itself is cleared the moment it is confirmed or dismissed, and the
  // copy read "…in ." while the dialog faded (QA4 BR N9).
  const [dialogLanguage, setDialogLanguage] = useState<string | null>(null);
  if (pendingLanguage !== null && pendingLanguage !== dialogLanguage) {
    setDialogLanguage(pendingLanguage);
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // The composer's visible attach control (B2): its files take the drop and
  // paste path (`handleComposerMediaFiles`), on every template and turn.
  const composerFileInputRef = useRef<HTMLInputElement>(null);

  const userProfile = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const rawUser = localStorage.getItem("user");
      if (!rawUser) {
        return null;
      }

      return JSON.parse(rawUser) as {
        avatarSeed?: string;
        email?: string;
        name?: string;
      };
    } catch {
      return null;
    }
  }, []);

  const avatarSeed =
    userProfile?.avatarSeed ??
    userProfile?.email ??
    userProfile?.name ??
    "user";
  const userInitials = useMemo(
    () => getUserInitials(userProfile?.name, userProfile?.email),
    [userProfile?.email, userProfile?.name],
  );

  const {
    appendAssistantPrompt,
    assumptions,
    cancelRequest,
    canStop,
    compiledSurveyJson,
    compilePersistedDraft,
    readiness,
    draftId,
    input,
    isDraftReady,
    isSavingStudyGoals,
    loadingDraft,
    canRetryLast,
    notEditableReason,
    messages,
    draft,
    persistDraftEdits,
    validateDraftEdits,
    retryLast,
    saveStudyGoals,
    sendMessage,
    sendCommand,
    sending,
    sendUnconfirmed,
    sessionError,
    setInput,
    showSlowWarning,
    persistOperations,
    draftRevision,
    presentation,
    startManualDraft,
    studyGoals,
    clarifications,
    declaredAssetTypes: serverDeclaredAssetTypes,
    inFlightTurn,
    turnStage,
    turnStageTotal,
    stopRequested,
    answerWithNextAudienceChange,
  } = useQuestionnaireDraftSession({
    goalId,
    sourceThreadToken,
    initialMessage,
    language,
    onSessionReady,
    resumeDraftId: resumeSessionId,
    selectedPopulation,
  });

  const quickEmbed = presentation === "quick" && isEmbeddedStudy;
  useEffect(() => {
    if (draftId && !loadingDraft) onPresentation?.(presentation);
  }, [draftId, loadingDraft, presentation, onPresentation]);
  const onSurveyReady = useCallback(async (compiled: ReviewCompile): Promise<boolean> => {
    if (!quickEmbed) return onFullSurveyReady(compiled);
    if (!draftId) return false;
    const refusal = reviewEntryRefusal(compiled, draftId);
    if (refusal || compiled.revision === null) {
      toast.error(refusal ?? t("questionnaire.reviewRetry"));
      return false;
    }
    postStudyEmbedMessage({ source: STUDY_EMBED_SOURCE, type: "quick-review-requested", draftToken: draftId, revision: compiled.revision });
    // The inline builder remains usable while the parent owns quote approval.
    return false;
  }, [quickEmbed, onFullSurveyReady, draftId, t]);

  // The stored files the session's draft assets hold (the server's draft, not
  // the editable copy: an unsaved editor edit does not offer or hide a file;
  // the workspace takes this `draft` as its `serverDraft`): the one answer to "is
  // this file already on an asset?" (`assetFileIds`), read by the composer's
  // staged tiles, the asset-confirm card and the offer of unplaced attachments.
  const heldFileIds = useMemo(
    () => assetFileIds(draft?.testedAssets?.assets),
    [draft],
  );
  // The composer's files no turn placed (L8): a file a turn's commit put on
  // an asset leaves, read off the user message that says so or the saved
  // draft's assets (a card's answer binds a file with no turn).
  const heldComposerFiles = useMemo(() => {
    const placed = new Set(
      messages.flatMap((message) =>
        message.role === "user"
          ? (message.attachments ?? []).flatMap((attached) =>
              attached.placedOn ? [attached.fileId] : [],
            )
          : [],
      ),
    );
    return composerFiles.filter((file) => {
      const token = composerUploads.get(file)?.token ?? "";
      return !placed.has(token) && !heldFileIds.has(token);
    });
  }, [composerFiles, composerUploads, heldFileIds, messages]);
  const composerFileTiles = useFileTiles(heldComposerFiles);
  // The latest user message's files and what its turn did with each (L8).
  const lastUserAttachments = useMemo(() => {
    const last = [...messages]
      .reverse()
      .find((message) => message.role === "user" && !isLocalMessage(message));
    return { id: last?.id ?? null, attachments: last?.attachments ?? [] };
  }, [messages]);

  // The design the draft's StudySpec resolved (plan S3.4 row 38). Nest
  // re-resolves it on every commit, so each loaded or saved draft reprices.
  const hasDraft = Boolean(draft);
  const specDesign = studySpecOf(draft)?.resolved.design ?? null;

  // The record the turn in flight answers reads as answered while it runs
  // (plan S4.1): the record is still open until the turn commits, and the
  // user has already given this answer. A collect card's intake writing its
  // answer, before that turn goes out, is answering it too.
  const answerInFlight = useMemo(
    () =>
      answerInFlightOf(
        messages,
        clarifications,
        inFlightTurn,
        intakeAnsweringCardId,
      ),
    [messages, clarifications, inFlightTurn, intakeAnsweringCardId],
  );
  const shownClarifications = answerInFlight.clarifications;
  // How each server card was answered, and which one is waiting, read from
  // the transcript's clarification records (plan S4.1), never from the
  // content of a neighbouring message.
  const answeredCards = useMemo(
    () => answeredCardsOf(messages, shownClarifications),
    [messages, shownClarifications],
  );
  const answeredByMessage = useMemo(
    () => cardsByAnswerMessage(answeredCards),
    [answeredCards],
  );
  // The opening-chain step that asked each card, so the card is worded in
  // the interface language from its code and option ids.
  const chainCodes = useMemo(
    () => chainCodesByMessage(clarifications),
    [clarifications],
  );
  const openCardId = openCardIdOf(shownClarifications);
  // Whether a question waits on an answer is the open record (L20), whatever
  // asked it and whether or not the draft is built, unless the turn in
  // flight is answering it.
  const openClarification = openClarificationOf({
    clarifications: shownClarifications,
  });
  const clarificationOpen = openClarification !== null;
  const askedAboutMessageId = openClarification?.requestMessageId ?? null;
  // A card shows its answered state once its record is answered (a local
  // card once it is picked); a superseded card stays unanswered.
  const answeredCardIds = useMemo(() => {
    const ids = new Set(localCardAnswers.keys());
    for (const [cardId, answer] of answeredCards) {
      if (answer.answered) ids.add(cardId);
    }
    return ids;
  }, [answeredCards, localCardAnswers]);
  // An asset-confirm card waits only while one of its files (its stored
  // upload; a file never stored is on no asset) is on no asset: it asks for a
  // file an asset holds no more, after a reload alike, and the composer
  // returns once its files are held. (`composerUploads` is the one map from a
  // card's file to its stored id; the offer effect fills it before a reload's
  // card exists.)
  const addAssetCardWaiting = useCallback(
    (cardId: string, action: LocalCardAction | undefined) =>
      action?.kind === "add_asset" &&
      !localCardAnswers.has(cardId) &&
      !action.files.every((file) =>
        heldFileIds.has(composerUploads.get(file)?.token ?? ""),
      ),
    [composerUploads, heldFileIds, localCardAnswers],
  );
  // A local card waits until it is picked. The "update the questions?" card
  // also waits only while one of its assets is still in no question, by the
  // rule that offered it (`uncoveredAssetsAmong` on the saved draft): once
  // the asset is removed, or a Save puts it in a question, it asks nothing
  // (QA4 EX2 N5: it stayed live after the new ad was removed).
  const isWaitingCard = useCallback(
    (message: { id: string }) => {
      if (!isLocalMessage(message)) return message.id === openCardId;
      if (localCardAnswers.has(message.id)) return false;
      const action = localCardActions.get(message.id);
      if (action?.kind === "add_asset") {
        return addAssetCardWaiting(message.id, action);
      }
      return (
        action?.kind !== "use_new_assets" ||
        uncoveredAssetsAmong(action.assetKeys, { draft, readiness }).length > 0
      );
    },
    [
      addAssetCardWaiting,
      draft,
      localCardActions,
      localCardAnswers,
      openCardId,
      readiness,
    ],
  );
  /** The answer a UI intercept gives the open card, by id (plan S4.1). */
  const interceptAnswer = useCallback(
    (card: { id: string; options?: Array<ChatMessageOption> }, value: string) => {
      const open = clarifications.find((record) => record.status === "open");
      if (!open || open.messageId !== card.id) return undefined;
      const optionId = card.options?.find((option) => option.value === value)?.id;
      return { clarificationId: open.id, ...(optionId ? { optionId } : {}) };
    },
    [clarifications],
  );

  useEffect(() => {
    if (!hasDraft) {
      return;
    }
    onStudyDesign?.(specDesign);
  }, [hasDraft, onStudyDesign, specDesign]);

  // Armed on first render when review sent us here with a prefilled re-plan.
  // Parent clears initialMessage after mount; the ref must not reset.
  const shouldReturnAfterReplanRef = useRef(
    Boolean(returnToStep && initialMessage?.trim()),
  );
  const hydratedCompileRef = useRef<SurveyJson | null | undefined>(undefined);
  const returnedToReviewRef = useRef(false);
  const replanSendStartedRef = useRef(false);
  const composerSendInFlightRef = useRef(false);
  const collectSubmitInFlightRef = useRef(false);

  useEffect(() => {
    if (!shouldReturnAfterReplanRef.current) {
      return;
    }
    if (hydratedCompileRef.current === undefined && compiledSurveyJson) {
      hydratedCompileRef.current = compiledSurveyJson;
    }
  }, [compiledSurveyJson]);

  useEffect(() => {
    if (
      !shouldReturnAfterReplanRef.current ||
      !onReturnToReview ||
      sending ||
      returnedToReviewRef.current
    ) {
      return;
    }
    if (!readiness?.canCompile || !compiledSurveyJson) {
      return;
    }
    if (compiledSurveyJson === hydratedCompileRef.current) {
      return;
    }
    returnedToReviewRef.current = true;
    void (async () => {
      // Review returns with the compile of the persisted draft and its
      // revision, as the Review click does, never the cached preview, and
      // through the same entry, which applies the verdict.
      const compiled = await compilePersistedDraft();
      const success = await onSurveyReady({
        ...compiled,
        title: draft?.title ?? null,
      });
      if (success) {
        onReturnToReview();
        return;
      }
      returnedToReviewRef.current = false;
    })();
  }, [
    compiledSurveyJson,
    compilePersistedDraft,
    draft?.title,
    onReturnToReview,
    onSurveyReady,
    readiness?.canCompile,
    sending,
  ]);

  useEffect(() => {
    if (!shouldReturnAfterReplanRef.current) {
      return;
    }
    if (sessionError) {
      shouldReturnAfterReplanRef.current = false;
      return;
    }
    if (sending) {
      replanSendStartedRef.current = true;
      return;
    }
    if (!replanSendStartedRef.current) {
      return;
    }
    const hasNewCompile =
      compiledSurveyJson != null &&
      compiledSurveyJson !== hydratedCompileRef.current;
    if (!hasNewCompile) {
      shouldReturnAfterReplanRef.current = false;
    }
  }, [compiledSurveyJson, sending, sessionError]);

  const { user: authUser } = useAuth();
  const whiteLabelEnabled =
    (authUser?.featureFlags as { whiteLabel?: boolean } | undefined)
      ?.whiteLabel === true;
  // Always the Fairgen twins icon — this AI avatar is never brand-swapped, even for themed accounts.
  const aiIconSrc = "/sidebar-brand-logo.svg";

  const workspace = useQuestionnaireWorkspace({
    assumptions,
    onSaveDraft: persistDraftEdits,
    onValidateDraft: validateDraftEdits,
    selectedPopulation,
    serverDraft: draft,
    serverRevision: draftRevision,
    serverSurveyJson: compiledSurveyJson,
  });
  // The study's asset panel, named as the panel heads itself: where a reply
  // points for what the chat could not do (F16).
  const assetPanelHeading = assetPanelLabel(
    workspace.conceptAssets,
    workspace.currentDraft?.testedAssets?.assetTypes ?? [],
    t,
  ).plural;
  // What a pasted link or a dropped file may become: the study's families,
  // from their one owner (`studyMaterialFamilies`, owner decision B2-4). The
  // recipe the saved draft records, else the goal's (before a draft has a
  // spec), and the families the catalog declares: the workspace's block once
  // there is one (a panel type switch lands there first), else the server
  // draft's, as on an intake draft with no workspace. CT-11: a concept study
  // was offered "Add as an ad / landing page / package".
  const declaredTypes =
    workspace.currentDraft?.testedAssets?.assetTypes ?? serverDeclaredAssetTypes;
  const studyFamilies = useMemo(
    () =>
      studyMaterialFamilies({
        recipe:
          studySpecOf(draft)?.recorded.recipe ??
          (goalId ? goalRecipe(goalId) : null),
        declaredTypes,
      }),
    [declaredTypes, draft, goalId],
  );
  const assetWritesPending =
    assetWritesInFlight > 0 || workspace.assetUploadsInFlight;

  // An asset-confirm card waits on its answer: the files dropped or pasted
  // ride on it. Its free-text box is open: words typed there answer the card
  // and carry its files (`handleDecisionSelect`).
  const assetCardOpen = useMemo(
    () =>
      [...localCardActions].some(([cardId, action]) =>
        addAssetCardWaiting(cardId, action),
      ),
    [addAssetCardWaiting, localCardActions],
  );

  // What leaving the page would lose, for the page's leave guards (the
  // browser's "Leave site?" and the sidebar's): the workspace's unsaved edits,
  // and an intake the user staged and has not added (the collect intake's
  // files or links, the composer's files, or a pasted file waiting on its
  // confirm card). Nothing else is: only the explicit Save writes, the draft
  // is otherwise persisted, the composer's unsent words are kept for the tab,
  // and a turn runs on the server.
  const objectiveEditorSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const savingObjectiveBufferRef = useRef(false);
  const [objectiveEditorOpen, setObjectiveEditorOpen] = useState(false);
  const onObjectiveEditorChange = useCallback(
    (editing: boolean, savePendingEdit?: () => Promise<boolean>) => {
      objectiveEditorSaveRef.current = editing ? savePendingEdit ?? null : null;
      setObjectiveEditorOpen(editing);
    },
    [],
  );
  const hasUnsavedWork =
    workspace.isDirty ||
    objectiveEditorOpen ||
    intakeStaged ||
    heldComposerFiles.length > 0 ||
    assetCardOpen;
  useStudyCreationBeforeUnload(hasUnsavedWork);
  useSidebarBackGuard(hasUnsavedWork);
  usePublishEmbedUnsavedWork(hasUnsavedWork);

  // The builder's "update the questions?" card, for the assets a write just
  // added that the write's readiness names as in no question
  // (`tested_asset_unused`, 04 I-19). One rule for every road that writes
  // assets, run once where every commit lands (`adoptedAssetKeysRef` below):
  // an asset the questions already take in gets no card. While a chat question waits on its answer the card
  // would take that question's place, so it is held until the question
  // closes (`heldNewAssetKeys`) and then asks again, by this same rule. The
  // card carries its own asset keys: Yes sends `use_new_assets` for exactly
  // those.
  const offerQuestionUpdate = useCallback(
    (
      addedKeys: ReadonlyArray<string>,
      accepted: {
        draft: unknown;
        readiness?: QuestionnaireDraftReadiness | null;
      },
    ) => {
      const uncovered = uncoveredAssetsAmong(addedKeys, accepted);
      if (uncovered.length === 0) return;
      if (clarificationOpen) {
        setHeldNewAssetKeys((current) => [
          ...new Set([
            ...current,
            ...uncovered.map((asset) => asset.stableKey),
          ]),
        ]);
        return;
      }
      const [{ type }] = uncovered;
      const card = newAssetsCardCopy(chatLanguage(), {
        family: uncovered.every((asset) => asset.type === type) ? type : null,
        count: uncovered.length,
      });
      const cardId = appendAssistantPrompt({
        content: card.content,
        options: [
          { label: card.updateLabel, value: NEW_ASSETS_CONFIRM.update },
          { label: card.addMyselfLabel, value: NEW_ASSETS_CONFIRM.addMyself },
        ],
      });
      setLocalCardActions((current) =>
        new Map(current).set(cardId, {
          kind: "use_new_assets",
          assetKeys: uncovered.map((asset) => asset.stableKey),
        }),
      );
    },
    [appendAssistantPrompt, clarificationOpen],
  );

  /**
   * The one rule after every commit that adds assets (BP-14), whichever road
   * wrote them: a Save, a chat-side asset write, an upload card's receipt
   * turn, or any other turn. The assets the adopted draft holds that the
   * draft adopted before it did not are offered the "update the questions?"
   * card (`offerQuestionUpdate`), read off the session's accepted draft and
   * its readiness, so no road runs it alone and none can skip it. The
   * page's load is the baseline, never an addition.
   */
  const adoptedAssetKeysRef = useRef<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (loadingDraft) return;
    const keys = assetKeysOf(draft?.testedAssets);
    const before = adoptedAssetKeysRef.current;
    adoptedAssetKeysRef.current = new Set(keys);
    if (before === null) return;
    const added = keys.filter((key) => !before.has(key));
    if (added.length > 0) offerQuestionUpdate(added, { draft, readiness });
  }, [draft, loadingDraft, offerQuestionUpdate, readiness]);

  /**
   * Every Save of the builder's edits, whichever road asks for it (the
   * panel's Save, "Save and continue" before a turn, Review, Edit audience or
   * an asset write). What it added gets the one post-commit rule above.
   */
  const saveEdits = useCallback(
    () => workspace.saveChanges(),
    [workspace],
  );

  // "Save and continue": a chat turn and Review work on the saved draft, so
  // unsaved edits are saved first, but only once the user says so. Resolves
  // "save" to save and continue, "discard" to drop the unsaved edits and
  // continue (offered instead of the Save when the server's dry run holds
  // the Save back, `saveHeldBack`), false to keep editing.
  type SaveContinueChoice = "save" | "discard" | false;
  const saveContinueResolverRef = useRef<((choice: SaveContinueChoice) => void) | null>(
    null,
  );
  const [saveContinueOpen, setSaveContinueOpen] = useState(false);
  const confirmSaveAndContinue = useCallback(
    () =>
      new Promise<SaveContinueChoice>((resolve) => {
        // One question at a time: an earlier unanswered ask keeps editing.
        saveContinueResolverRef.current?.(false);
        saveContinueResolverRef.current = resolve;
        setSaveContinueOpen(true);
      }),
    [],
  );
  const settleSaveContinue = useCallback((choice: SaveContinueChoice) => {
    const resolve = saveContinueResolverRef.current;
    saveContinueResolverRef.current = null;
    setSaveContinueOpen(false);
    resolve?.(choice);
  }, []);
  const saveHeldBack = workspace.isDirty ? (workspace.saveHeldBack ?? null) : null;
  const discardWorkspaceChanges = workspace.discardChanges;
  /**
   * Save unsaved edits before something that works on the saved draft, once
   * the user agrees. Resolves true when there was nothing to save or the
   * save succeeded.
   */
  const savePendingBuffers = useCallback(async (
    onWorkspaceSaved?: (saved: WorkspaceSaved) => void,
  ): Promise<boolean> => {
    const saveObjectiveEditor = objectiveEditorSaveRef.current;
    if (workspace.isDirty) {
      // Refused or failed: saveChanges has said why (toast, card chips).
      const outcome = await saveEdits();
      if (outcome.status === "saved") onWorkspaceSaved?.(outcome);
      else if (outcome.status !== "nothing_to_save") return false;
    }
    if (!saveObjectiveEditor) return true;
    savingObjectiveBufferRef.current = true;
    try {
      return await saveObjectiveEditor();
    } finally {
      savingObjectiveBufferRef.current = false;
    }
  }, [saveEdits, workspace.isDirty]);
  const saveEditsIfConfirmed = useCallback(async (
    onWorkspaceSaved?: (saved: WorkspaceSaved) => void,
  ): Promise<boolean> => {
    if (!workspace.isDirty && !objectiveEditorSaveRef.current) return true;
    const choice = await confirmSaveAndContinue();
    if (!choice) return false;
    if (choice === "discard") {
      discardWorkspaceChanges();
      const saveObjectiveEditor = objectiveEditorSaveRef.current;
      return saveObjectiveEditor ? saveObjectiveEditor() : true;
    }
    return savePendingBuffers(onWorkspaceSaved);
  }, [confirmSaveAndContinue, discardWorkspaceChanges, savePendingBuffers, workspace.isDirty]);
  // The flow's own control that leaves this draft (the "Continue draft"
  // banner) asks the same "Save and continue" first (F26 #11).
  useEffect(() => {
    if (!saveFirstRef) return undefined;
    saveFirstRef.current = saveEditsIfConfirmed;
    return () => {
      if (saveFirstRef.current === saveEditsIfConfirmed) {
        saveFirstRef.current = null;
      }
    };
  }, [saveEditsIfConfirmed, saveFirstRef]);
  // Editing the audience leaves the builder; unsaved edits are saved first,
  // or the user stays.
  const editAudience = useCallback(async () => {
    if (!onEditAudience) return;
    if (!(await saveEditsIfConfirmed())) return;
    onEditAudience();
  }, [onEditAudience, saveEditsIfConfirmed]);
  // The objective panel's Save is a `set_objective` turn (plan S4.2, L23),
  // so it works on the saved draft too. Resolves whether the turn committed:
  // false when nothing was sent, or the turn failed or was stopped, and the
  // row reopens its editor on the text.
  const saveObjectiveAfterEdits = useCallback(
    async (next: StudyObjectiveUpdate): Promise<boolean> => {
      if (
        !savingObjectiveBufferRef.current &&
        !(await saveEditsIfConfirmed())
      ) return false;
      return saveStudyGoals(next);
    },
    [saveEditsIfConfirmed, saveStudyGoals],
  );
  // The "Study brand" chip (plan Decision 5): the brand the saved draft's
  // spec resolves to, and the user's edit as one `set_study_brand` write on
  // the saved draft (unsaved edits are saved first).
  const studyBrand = useMemo(
    () => resolveStudyBrand(studySpecOf(draft)?.recorded.context),
    [draft],
  );
  const saveStudyBrand = useCallback(
    async (brandName: string | null): Promise<boolean> => {
      if (!(await saveEditsIfConfirmed())) return false;
      try {
        await persistOperations(
          [{ op: "set_study_brand", brandName }],
          // Replay rule: the latest draft already holds this exact user edit.
          (latest) =>
            latest.studyBrand?.source === "user_edit" &&
            (latest.studyBrand.brandName ?? null) === brandName,
        );
        return true;
      } catch {
        toast.error(t("questionnaire.studyBrand.saveFailed"));
        return false;
      }
    },
    [persistOperations, saveEditsIfConfirmed, t],
  );
  // A retry sends the failed turn again, on the saved draft.
  const retryLastAfterEdits = useCallback(
    () => retryLast(() => saveEditsIfConfirmed()),
    [retryLast, saveEditsIfConfirmed],
  );
  // The one busy fact the chat's controls lock on: something is writing the
  // draft (a turn, a Save, a chat-side asset write, or the request that opens
  // the workspace from Build manually / Paste), or the page is still
  // loading it. The composer, the card's options, Edit audience, the language
  // pick, Review and the panel's own lock all read it, because each either
  // writes the draft itself or needs a Save that cannot run twice.
  // `sending` is the session's turn in flight (its `inFlightTurn`), live or
  // re-attached after a reload; the load is its own fact, so it shows no
  // typing dots or Stop and never unlocks a turn.
  //
  // The fact carries its cause, so a locked control can say what it waits
  // on. When two overlap (a collect receipt's turn during its asset write,
  // a turn after its "Save and continue"), the turn is named first.
  const draftArchived = notEditableReason === "archived";
  const turnInFlight = sending && !draftArchived;
  const busyCause: BuilderBusyCause | null = turnInFlight
    ? "turn"
    : workspace.isSaving
      ? "save"
      : assetWritesPending
        ? "asset_write"
        : isOpeningWorkspace
          ? "opening"
          : loadingDraft
            ? "loading"
            : notEditableReason
              ? "read_only"
              : null;
  const chatInputDisabled = busyCause !== null;
  // A card held back by a chat question asks once that question has closed
  // and nothing runs (the turn that answers it has committed), for the held
  // assets the saved readiness still names as in no question.
  useEffect(() => {
    if (heldNewAssetKeys.length === 0 || clarificationOpen || busyCause) return;
    setHeldNewAssetKeys([]);
    offerQuestionUpdate(heldNewAssetKeys, { draft, readiness });
  }, [
    busyCause,
    clarificationOpen,
    draft,
    heldNewAssetKeys,
    offerQuestionUpdate,
    readiness,
  ]);
  const composerLockCause: BuilderLockCause | null = busyCause;
  const buildProgressLine =
    busyCause === "turn"
      ? emptyCanvasBusyLine("turn", turnStage, {
          stopRequested,
          total: turnStageTotal,
        })
      : null;
  const composerLocked = composerLockCause !== null;

  // ── Coverage assessment ────────────────────────────────────────────────────
  // The server scores the accepted revision and caches its verdicts, so
  // coverage refreshes after a Save or an agent turn, never from unsaved edits.
  const coverageByQuestionId = useQuestionnaireCoverage({
    draftId,
    revision: draftRevision,
    questionIds: (
      (draft?.researchQuestions ?? []) as Array<{ id: string }>
    ).map((question) => question.id),
  });
  // ── End coverage assessment ────────────────────────────────────────────────

  // The readiness lines the chat shows (the blocking-issue line under the
  // composer) speak the chat's language; the panel's chips and cards, which
  // call `formatIssueCopy` without one, read in the interface's.
  const threadLanguage = chatLanguage();
  const issueCopyContext = useMemo(
    () => ({
      resolvedMinimum: readiness?.resolvedMinimum,
      draftId,
      lng: threadLanguage,
    }),
    [draftId, readiness?.resolvedMinimum, threadLanguage],
  );
  // The server's verdict, and its `clarification_open` still gates Review
  // and Launch. While that record is being answered (the turn in flight, or
  // the collect intake's write before it), the banner and Review's tooltip do
  // not ask the user for the answer again (plan S4.1).
  const shownBlockingIssues = useMemo(
    () =>
      answerInFlight.answering
        ? readiness?.blockingIssues?.filter(
            (issue) => issue.code !== "clarification_open",
          )
        : readiness?.blockingIssues,
    [answerInFlight.answering, readiness?.blockingIssues],
  );
  const indexedIssues = useMemo(() => {
    if (workspace.isDirty || !readiness) {
      return indexBlockingIssuesByQuestionId(undefined, null);
    }
    return indexBlockingIssuesByQuestionId(
      shownBlockingIssues,
      workspace.savedDraft ?? null,
    );
  }, [readiness, shownBlockingIssues, workspace.isDirty, workspace.savedDraft]);
  // The verdict's advice (QA4 N3): computed on every Save and shown nowhere
  // before. Indexed the same way, onto the questions and the items it names,
  // and read only while the draft on screen is the saved one it describes.
  const indexedAdvisories = useMemo(
    () =>
      workspace.isDirty || !readiness
        ? indexBlockingIssuesByQuestionId(undefined, null)
        : indexBlockingIssuesByQuestionId(
            readiness.advisoryIssues,
            workspace.savedDraft ?? null,
          ),
    [readiness, workspace.isDirty, workspace.savedDraft],
  );

  useEffect(() => {
    const issues = readiness?.blockingIssues;
    if (!issues?.length) {
      return;
    }
    for (const issue of issues) {
      const formatted = formatIssueCopy(issue, issueCopyContext);
      if (!formatted.mapped) {
        track(AnalyticsEvents.QUESTIONNAIRE_ISSUE_UNMAPPED, {
          code: formatted.code,
        });
      }
    }
  }, [issueCopyContext, readiness?.blockingIssues]);

  /**
   * Every chat turn goes out on the saved draft: unsaved edits are saved
   * first, once the user agrees. Nothing on the asset cards holds a turn
   * back: the server's applier and readiness are the one verdict on an
   * asset, and "remove claim 3" is how a blank card gets fixed (QA4 FE3).
   * `onBlocked` runs when the turn did not go out. Resolves whether the turn
   * committed: false when it was blocked, or it failed or was stopped.
   */
  const sendGuardedChatTurn = useCallback(
    async (
      message: string | { command: QuestionnaireTurnCommand },
      extras?: {
        onBlocked?: () => void;
        optionId?: string;
        /** The words are the composer's (the session's `sendMessage`). */
        from?: "composer";
        /** The stored files the words carry (L8). */
        attachments?: Array<QuestionnaireTurnAttachmentInput>;
      },
    ): Promise<boolean> => {
      if (assetWritesPending) {
        extras?.onBlocked?.();
        return false;
      }
      setInvitePaste(false);
      if (!(await saveEditsIfConfirmed())) {
        extras?.onBlocked?.();
        return false;
      }
      const result = await resolveDraftForChatSend(
        {
          ...workspace,
          // The shared continuation gate above has already saved every local
          // buffer it found, so this helper must not ask a second time from
          // the stale render that started the action.
          isDirty: false,
          saveChanges: saveEdits,
        },
        {
          onCancelled: () => extras?.onBlocked?.(),
          // saveChanges has already said why (its outcome is typed); a second
          // toast would talk over it.
          onSaveFailure: () => extras?.onBlocked?.(),
        },
      );
      if (!result.ok) {
        return false;
      }
      return typeof message === "string"
        ? sendMessage(message, extras?.optionId, extras?.from, extras?.attachments)
        : sendCommand(message.command);
    },
    [
      assetWritesPending,
      confirmSaveAndContinue,
      saveEdits,
      saveEditsIfConfirmed,
      sendCommand,
      sendMessage,
      workspace,
    ],
  );

  // The questionnaire's language is the committed draft's (`contentLanguage`,
  // stamped by the build and by a translation's commit). The chip, Review and
  // the launch read creation state, so it takes the draft's language whenever
  // a committed draft names a new one, including a translation that settles
  // after a reload. Until a draft names one, the pick below stands.
  const committedLanguage = draft?.contentLanguage;

  /**
   * Language change guard. Locked while anything writes the draft (a turn
   * above all): that turn commits in the language it was sent with. Before
   * the draft names a language the pick is free (it is the language the
   * build writes in). Once it does, the pick is a `translate` turn, which
   * the draft records at commit: with questions it is an AI translation pass
   * (confirm first), with none the server records the language and no model
   * runs (C12-2).
   */
  const handleLanguageSelect = useCallback(
    (lang: string) => {
      if (!onLanguageChange || lang === language) {
        return;
      }
      if (chatInputDisabled) {
        toast.info("Hang on — let the current response finish first.");
        return;
      }
      if (workspace.questionCount === 0) {
        if (committedLanguage) {
          void sendGuardedChatTurn({
            command: { kind: "translate", language: lang },
          });
        } else {
          onLanguageChange(lang);
        }
        return;
      }
      setPendingLanguage(lang);
    },
    [
      chatInputDisabled,
      committedLanguage,
      language,
      onLanguageChange,
      sendGuardedChatTurn,
      workspace.questionCount,
    ],
  );

  // A typed command (plan S4.2): what translating means is the revision
  // prompt's to say, not a sentence the builder writes. It works on the saved
  // draft, so unsaved edits are saved first (Save and continue). The language
  // does not change here: the translation records it on the draft at commit,
  // and the builder takes it from the committed draft (below), so a failed,
  // stopped or blocked translation leaves it as it was.
  const confirmLanguageTranslate = useCallback(() => {
    if (!pendingLanguage) {
      return;
    }
    const nextLanguage = pendingLanguage;
    setPendingLanguage(null);
    void sendGuardedChatTurn({
      command: { kind: "translate", language: nextLanguage },
    });
  }, [pendingLanguage, sendGuardedChatTurn]);

  // The one derivation from the committed draft to creation state (C12-2).
  useEffect(() => {
    if (!committedLanguage) {
      return;
    }
    onLanguageChange?.(committedLanguage);
  }, [committedLanguage, onLanguageChange]);

  /**
   * The asset-intake core for BOTH the opening collect card and the composer
   * (a typed URL, a pasted or dropped file), D2 S5.4. The browser hands over
   * bytes and links; the server names, reads and reports what it collected:
   * - each file is uploaded and issued (a PDF with its page-1 preview);
   * - each page link is captured once for its preview and issued, so the Save
   *   never captures it again; a direct video link goes as a link;
   * - the new assets are one `collect_assets` operation, applied by the server
   *   (`asset_collected` and the links it skipped come back as effects), or
   *   carried by the card's durable receipt (`deferPersist`);
   * - an attach card (F19) puts each file on its item's assets instead.
   * Unsaved edits are saved first, once the user agrees ("Save and continue").
   * It does NOT refresh the UI or send chat: the caller decides.
   */
  const addCollectedAssets = useCallback(
    async (
      collect: Pick<ChatMessageCollect, "assetType" | "attachTo">,
      values: {
        urls: Array<string>;
        files: Array<File>;
        /** Pages the intake captured for their preview, each issued as a file. */
        captured?: Array<{ url: string; file: IssuedAssetFile }>;
        /** On an attach card: the item each file goes on, file by file. */
        targets?: Array<ChatMessageCollectAttachItem>;
      },
      deferPersist = false,
    ): Promise<
      | {
          currentDraft: QuestionnaireDraft | null;
          /**
           * The saved catalog the write started from, or the accepted one
           * after an immediate write. `currentDraft` is null until the first
           * instrument is built.
           */
          assetCatalog: TestedAssetBlock | null;
          revision?: number;
          /** The keys of the assets this write added (its `asset_collected` effects). */
          addedAssetKeys: Array<string>;
          /**
           * Links refused with a written reason (a video host we cannot
           * read): never worth attempting.
           */
          refusedUrls: Array<{ url: string; reason: string }>;
          /**
           * What the Save of the collect reported (`asset_collected`, and a
           * link it skipped). Empty when the receipt turn carries the collect:
           * its stored message carries them, shown with it in the transcript.
           */
          effects: Array<QuestionnaireOperationEffect>;
          /** On an attach write: the items it put a file on (F19). */
          attachedItems?: Array<ChatMessageCollectAttachItem>;
          /**
           * On an attach write: its operations, saved or carried by the
           * receipt; what its receipt lines read (`attachReceiptLinesOf`).
           */
          attachOperations?: Array<QuestionnaireCollectedAssetOperation>;
          /** Typed asset mutations carried by the durable receipt turn. */
          assetOperations?: Array<QuestionnaireCollectedAssetOperation>;
          blocked?: never;
        }
      | { blocked: "no_draft" | "save_failed" | "cancelled" }
    > => {
      if (!draftId) return { blocked: "no_draft" };
      // The saved draft, never the builder's unsaved copy.
      let baseDraft = draft ?? workspace.savedDraft ?? null;
      let savedForContinuation:
        | WorkspaceSaved
        | undefined;
      if (!(await saveEditsIfConfirmed((saved) => {
        savedForContinuation = saved;
      }))) {
        return { blocked: "cancelled" };
      }
      if (savedForContinuation?.draft) {
        baseDraft = savedForContinuation.draft;
      }
      const savedBlock =
        (baseDraft?.testedAssets as TestedAssetBlock | undefined) ?? null;
      // A link whose capture failed: a page keeps it as its link (below).
      const failedLinks: Array<string> = [];
      const refusedUrls: Array<{ url: string; reason: string }> = [];
      // In the order the assets are named: video links, files, page captures.
      const videoItems: Array<LinkedCollectedMedia> = [];
      const fileItems: Array<CollectedAssetMediaRef> = [];
      const captureItems: Array<LinkedCollectedMedia> = [];
      for (const url of collectableLinks(values.urls)) {
        // FIRST, before any capture. A Vimeo / Drive / Dropbox link screenshots
        // as a still of a player page: name the host and say what to do.
        const blocked = nonKeyframeableVideoMessage(url);
        if (blocked) {
          refusedUrls.push({ url, reason: blocked });
          continue;
        }
        // A link straight at video bytes IS the asset: nothing to capture.
        const videoRef = mediaRefFromVideoUrl(url);
        if (videoRef) {
          videoItems.push({ mediaRef: videoRef, sourceUrl: url });
          continue;
        }
        try {
          const capture = await captureQuestionnaireUrlApi(url, {
            scale: 1,
            fullPage: true,
          });
          if (capture.ok && capture.file) {
            captureItems.push({
              mediaRef: mediaRefFromIssuedFile(capture.file),
              sourceUrl: url,
            });
          } else {
            failedLinks.push(url);
          }
        } catch (error) {
          const detail = error instanceof Error ? error.message : String(error);
          console.warn("[questionnaire-chat] URL capture failed", {
            url,
            message: detail,
          });
          failedLinks.push(url);
        }
      }
      // A page link is an asset on its own: the Save captures it again and
      // readiness says what it needs (`savesCaptureOfLink`). It is collected
      // as its link, never refused here; only an attach card, which puts a
      // picture on an item, has nothing to place without one.
      const linkedPages =
        !collect.attachTo && savesCaptureOfLink(collect.assetType as TestedAssetType)
          ? failedLinks.splice(0)
          : [];
      for (const { url, file } of values.captured ?? []) {
        captureItems.push({ mediaRef: mediaRefFromIssuedFile(file), sourceUrl: url });
      }
      for (const file of values.files) {
        // The upload policy, before the bytes leave (B23): a refused file is
        // named with its reason, and the rest are still collected.
        const refusal = assetUploadRefusalLine(file);
        if (refusal) {
          toast.error(refusal);
          continue;
        }
        // A composer file its send already stored is that upload (L8).
        const upload =
          composerUploads.get(file) ?? (await uploadQuestionnaireAssetFileApi(file));
        const mediaRef = mediaRefFromUpload(upload, file);
        // A PDF's page 1, rendered and issued by the upload.
        fileItems.push({
          ...mediaRef,
          ...(upload.preview
            ? {
                clientPreviewMediaRefs: [
                  mediaRefFromPdfPreview(upload.preview, mediaRef),
                ],
              }
            : {}),
        });
      }
      const staged = {
        videoLinks: videoItems,
        files: fileItems,
        captures: captureItems,
        pageLinks: linkedPages,
      };
      const nothingCollected = {
        currentDraft: null,
        assetCatalog: savedBlock,
        addedAssetKeys: [],
        refusedUrls,
        effects: [],
      };
      // An attach card (F19): each file becomes the media of every asset of
      // the item the user put it on (each price version of a product alike),
      // so the write is only those assets' media and nothing is added. With
      // nothing staged it writes nothing: the user continued without a file.
      if (collect.attachTo) {
        const placements = attachPlacementsOf(
          savedBlock,
          collect,
          stagedMediaRefsOf(staged),
          values.targets,
        );
        const preparedBlock = savedBlock
          ? (attachCollectedMedia(
              savedBlock as unknown as Record<string, unknown>,
              placements,
            ) as unknown as TestedAssetBlock)
          : null;
        const operations = (
          savedBlock && preparedBlock
            ? diffTestedAssetsToOperations(savedBlock, preparedBlock)
            : []
        ) as Array<QuestionnaireCollectedAssetOperation>;
        const accepted =
          !deferPersist && operations.length > 0
            ? await persistOperations(
                operations,
                carriesTestedAssets(preparedBlock),
              )
            : { draft: baseDraft, revision: undefined };
        return {
          ...nothingCollected,
          currentDraft: accepted.draft,
          revision: accepted.revision,
          assetCatalog: deferPersist
            ? preparedBlock
            : (accepted.draft?.testedAssets as TestedAssetBlock | undefined) ??
              null,
          attachedItems:
            operations.length > 0 ? placements.map(({ item }) => item) : [],
          attachOperations: operations,
          ...(deferPersist ? { assetOperations: operations } : {}),
        };
      }
      const items = collectedItemsOf(staged);
      if (items.length === 0) return nothingCollected;
      const operation = collectAssetsOperation(collect, items);
      // The durable receipt carries the operation; the turn applies and
      // reads it before PLAN, in one revision with the receipt.
      if (deferPersist) {
        return {
          ...nothingCollected,
          currentDraft: baseDraft,
          assetOperations: [operation],
        };
      }
      // A Save that carries a video watches it: the existing "reading the
      // ad" line covers the wait.
      const watches = items.some((item) =>
        "media" in item
          ? item.media.some((ref) => ref.kind === "video")
          : isDirectVideoUrl(item.url),
      );
      if (watches) setReadingCreative(true);
      let accepted: Awaited<ReturnType<typeof persistOperations>>;
      try {
        accepted = await persistOperations(
          [operation],
          carriesCollectedItems(items),
        );
      } finally {
        if (watches) setReadingCreative(false);
      }
      const effects = accepted.effects ?? [];
      return {
        currentDraft: accepted.draft,
        revision: accepted.revision,
        assetCatalog:
          (accepted.draft?.testedAssets as TestedAssetBlock | undefined) ??
          null,
        addedAssetKeys: effects.flatMap((effect) =>
          effect.kind === "asset_collected" && effect.assetStableKey
            ? [effect.assetStableKey]
            : [],
        ),
        refusedUrls,
        effects,
      };
    },
    [
      composerUploads,
      draft,
      draftId,
      persistOperations,
      saveEditsIfConfirmed,
      workspace,
    ],
  );

  /**
   * What the server's collect skipped (D2): a video host it cannot read, or a
   * link already attached. One path for both answers a collect gets: the
   * Save's effects, and the effects its durable receipt's message carries.
   * Says nothing when it skipped nothing; returns whether it said anything.
   */
  // A Save's skips (it writes no message); a receipt turn's are its stored
  // message's, shown with it in the transcript.
  const sayCollectedSkips = useCallback(
    (effects: ReadonlyArray<QuestionnaireOperationEffect>): boolean => {
      const lines = userMessageReceiptLines(
        { effects },
        chatT(chatLanguage()),
      );
      for (const content of lines) appendAssistantPrompt({ content });
      return lines.length > 0;
    },
    [appendAssistantPrompt],
  );

  const handleCollectSubmit = useCallback(
    async (
      message: { id: string; collect: ChatMessageCollect },
      values: {
        urls: Array<string>;
        files: Array<File>;
        captured?: Array<{ url: string; file: IssuedAssetFile }>;
        targets?: Array<ChatMessageCollectAttachItem>;
        companionMessage?: string;
      },
    ) => {
      if (!draftId || collectSubmitInFlightRef.current) return;
      collectSubmitInFlightRef.current = true;
      setAssetWritesInFlight((n) => n + 1);
      setIntakeAnsweringCardId(message.id);
      try {
        const result = await addCollectedAssets(message.collect, {
          urls: values.urls,
          files: values.files,
          captured: values.captured,
          targets: values.targets,
        }, true);
        // Nothing was collected: the intake keeps what the user staged. A
        // failed save has already said why.
        if (result.blocked === "cancelled" || result.blocked === "save_failed")
          return false;
        if (result.blocked) {
          toast.error(
            "Start your questionnaire first, then attach your files.",
          );
          return;
        }
        // A refused URL never became an asset, so say so out loud. Silence here
        // is the exact failure this branch exists to kill: a Vimeo link used to
        // land as a screenshot of a player page and the questionnaire was
        // written about an ad nobody read.
        for (const refused of result.refusedUrls) {
          toast.error(refused.reason);
        }
        // The durable receipt carries the prepared mutations and identifies
        // the assets answering this card; one that collects is named by the
        // server once applied. Nothing collected: keep the intake. An attach
        // card can also be answered by continuing without a file.
        const collects = Boolean(
          result.assetOperations?.some(
            (operation) => operation.op === "collect_assets",
          ),
        );
        const assetKeys = collects
          ? []
          : receiptAssetKeysOf(result.assetCatalog, message.collect);
        if (
          assetKeys.length === 0 &&
          !collects &&
          !message.collect.attachTo &&
          !message.collect.optional
        )
          return false;
        await sendCommand({
          kind: "collect_receipt",
          assetKeys,
          ...(values.companionMessage
            ? { userMessage: values.companionMessage }
            : {}),
          ...(result.assetOperations?.length
            ? { assetOperations: result.assetOperations }
            : {}),
        });
      } catch (error) {
        console.error("Failed to add collected assets:", error);
        // The Save's mapper: a refused write reads as it does on Save.
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Failed to add your files",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        throw error; // CollectCard stays editable for a retry.
      } finally {
        collectSubmitInFlightRef.current = false;
        setAssetWritesInFlight((n) => n - 1);
        setIntakeAnsweringCardId(null);
      }
    },
    [draftId, addCollectedAssets, sendCommand, workspace.savedDraft],
  );

  // Composer asset intake: a file pasted or dropped on the composer becomes a
  // tested asset of the family the confirm card's option names. The option is the one action
  // (QA4 AD N6): nothing goes to the chat beside it. A message with words is
  // the user's message and goes to the chat as it is; it never reaches here,
  // so no words wait on this write (a brief that opens "Test a Pepsi video ad
  // ..." is the planner's to read, whole).
  const handleChatAssetAdd = useCallback(
    async (
      assetType: TestedAssetType,
      values: { files: Array<File> },
      /**
       * Nothing was added ("Keep editing", a refused write): the confirm card
       * asks again, with its files.
       */
      onNotAdded: () => void,
    ) => {
      const noun = assetFamilyNoun(assetType);
      // Capturing a page screenshot (or uploading images) takes a few seconds with
      // no other on-screen signal, so show a dismissable loading toast meanwhile.
      const toastId = toast.loading(`Adding the ${noun}…`);
      setAssetWritesInFlight((n) => n + 1);
      try {
        const sendsReceipt = Boolean(studyGoals) &&
          (workspace.currentDraft?.researchQuestions?.length ?? 0) === 0;
        const result = await addCollectedAssets(
          { assetType },
          { urls: [], files: values.files },
          sendsReceipt,
        );
        toast.dismiss(toastId);
        if (result.blocked) {
          // Nothing was added: the confirm card asks again. A failed save has
          // already said why; the card only opens once a draft exists.
          onNotAdded();
          return;
        }
        const saidSkips = sayCollectedSkips(result.effects);
        if (result.assetOperations?.length) {
          // Before the questions, once the objective exists: the collect
          // card's own receipt, so intake advances. The turn applies and
          // reads what it carries, names what it added, and says what it
          // skipped as the Save does.
          await sendCommand({
            kind: "collect_receipt",
            assetKeys: [],
            assetOperations: result.assetOperations,
          });
        } else if (result.addedAssetKeys.length === 0) {
          if (!saidSkips) {
            appendAssistantPrompt({
              content: alreadyAttachedLine(assetType, chatLanguage()),
            });
          }
        } else if (
          (workspace.currentDraft?.researchQuestions?.length ?? 0) === 0
        ) {
          // With no questions yet and no objective, "update the questions"
          // would be a card whose Yes sends a canned turn the intake chain
          // would consume as the answer to whatever it is asking: re-ask
          // the objective instead. With questions, the one post-commit rule
          // offers the card for what the write added.
          appendAssistantPrompt({
            content: addedBackToObjectiveLine(
              assetType,
              result.addedAssetKeys.length,
              chatLanguage(),
            ),
          });
        }
      } catch (error) {
        toast.dismiss(toastId);
        console.error("Failed to add asset from chat:", error);
        // The Save's mapper: a refused write reads as it does on Save.
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Couldn't add that — please try again.",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        // Nothing was added: the confirm card asks again with its files.
        onNotAdded();
      } finally {
        setAssetWritesInFlight((n) => n - 1);
      }
    },
    [
      addCollectedAssets,
      appendAssistantPrompt,
      sayCollectedSkips,
      sendCommand,
      draftId,
      studyGoals,
      workspace.currentDraft,
      workspace.savedDraft,
    ],
  );

  // The confirm card's "Add it to <item>" (one file) or "Add them to the items
  // without a picture" (several files): each file becomes an item's picture by
  // the attach card's own write
  // (F19, `addCollectedAssets` with `collect.attachTo`), on every price
  // version of a product alike, in the items' order; nothing is added. The
  // write never replaces a file an item got in between. Its receipt says which
  // file went on which item. The items are already in the questions, so no
  // card follows.
  const handleChatAssetAttach = useCallback(
    async (
      targets: ReadonlyArray<MaterialAttachOffer>,
      values: { files: Array<File> },
      /** Nothing was written: the confirm card asks again. */
      onNotAdded: () => void,
    ) => {
      const items = targets.map(({ item }) => item);
      // File by file, the item it goes on: each takes `FILES_PER_ASSET`.
      const placed = values.files.map(
        (_file, index) => items[Math.floor(index / FILES_PER_ASSET)],
      );
      const toastId = toast.loading("Adding the file…");
      setAssetWritesInFlight((n) => n + 1);
      try {
        const result = await addCollectedAssets(
          { assetType: targets[0].family, attachTo: items },
          { urls: [], files: values.files, targets: placed },
        );
        toast.dismiss(toastId);
        if (result.blocked) {
          onNotAdded();
          return;
        }
        const attached = result.attachedItems ?? [];
        const receipt = attachReceiptLinesOf(
          { attachTo: attached },
          result.attachOperations ?? [],
        ).map((line) => attachReceiptLine(line, chatLanguage()));
        // An item that got its file in between keeps it (the write skipped it).
        const taken = items
          .filter((item) => !attached.includes(item))
          .map((item) => alreadyHasFileLine(item.label, chatLanguage()));
        if (receipt.length + taken.length > 0) {
          appendAssistantPrompt({ content: [...receipt, ...taken].join("\n") });
        }
      } catch (error) {
        toast.dismiss(toastId);
        console.error("Failed to attach a file from chat:", error);
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Couldn't add that — please try again.",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        onNotAdded();
      } finally {
        setAssetWritesInFlight((n) => n - 1);
      }
    },
    [addCollectedAssets, appendAssistantPrompt, draftId, workspace.savedDraft],
  );

  /**
   * The asset-confirm card for files handed over: what they can become in
   * this study (`materialOffers`, the registry's typed facts), asked before
   * anything is written, and handled by `tryHandleLocalCard`. A link is not
   * offered here: the collect card takes it, and the chat's `add_asset`
   * carries it. A family with room for fewer than were given is never offered: a line says the limit and asks
   * which to add, and the files stay staged for the words that name them. When nothing can
   * take them, a line says the limit, or asks what they show, instead.
   * Returns whether a card took them: after a line, they are still the
   * caller's.
   */
  const offerMaterial = useCallback(
    (
      values: { files: Array<File> },
      families: ReadonlyArray<TestedAssetType> | null = studyFamilies,
    ): boolean => {
      const count = values.files.length;
      const offers: MaterialOffers = materialOffers(
        count,
        { families, assets: workspace.currentDraft?.testedAssets?.assets ?? [] },
        respondentWordingFor(workspace.currentDraft?.contentLanguage),
      );
      const lng = chatLanguage();
      // A family with room for fewer than were given is not offered: the study
      // never takes a subset the user did not pick (PP-14: ask which).
      const addAs = offers.addAs.filter(({ room }) => room >= count);
      const overRoom = offers.addAs.filter(({ room }) => room < count);
      // What the items take (F19), by the attach card's one computation.
      const capacity = attachCapacityOf(offers.attachTo);
      const attachEach =
        count > 1 && count <= capacity
          ? offers.attachTo.slice(0, Math.ceil(count / FILES_PER_ASSET))
          : [];
      const attachOne = count === 1 ? offers.attachTo : [];
      // More than the items take: the card says so and the user picks the
      // one to use; the rest stay out, named (BP-01).
      const given = values.files.map((file) => file.name);
      const pickFrom =
        offers.attachTo.length > 0 && count > capacity ? given : [];
      if (
        addAs.length === 0 &&
        attachOne.length === 0 &&
        attachEach.length === 0 &&
        pickFrom.length === 0
      ) {
        // Nothing the study uses can take it (B2): never a family the study
        // does not test. Its words make the item, and the model asks for the
        // file on the reply's upload card, whose intake takes the file the
        // caller still holds.
        appendAssistantPrompt({
          content:
            overRoom.length > 0
              ? overRoom.map(({ family }) => whichToAddLine(family, lng)).join(" ")
              : offers.atLimit.length > 0
                ? offers.atLimit.map((limited) => typeCapLine(limited, lng)).join(" ")
                : describeItLine(lng),
        });
        return false;
      }
      const kind = values.files.every((file) => isSupportedVideoMime(file.type))
        ? "video"
        : values.files.every((file) => file.type.startsWith("image/"))
          ? "image"
          : "file";
      // With one item, a pick names it; with several, the file picked is
      // offered again on its own, and that card asks for its item.
      const pickFor =
        pickFrom.length > 0 && offers.attachTo.length === 1
          ? offers.attachTo[0].item.label
          : null;
      const cardId = appendAssistantPrompt({
        // A family over its room is named on the card, with the ask for which:
        // the card never drops it silently.
        content: [
          ...overRoom.map(({ family }) => whichToAddLine(family, lng)),
          materialCardQuestion(lng, {
            given: kind,
            count,
            pick: pickFrom.length === 0 ? "none" : pickFor !== null ? "one" : "each",
            pickItem: pickFor,
          }),
        ].join(" "),
        options: [
          ...pickFrom.map((name, index) => ({
            label: useFileLabel(name, pickFor, lng),
            value: pickGivenOptionValue(index),
          })),
          ...addAs.map(({ family: offered }) => ({
            // Offered only when its registry materials take a file or a link:
            // an ad, a landing page or a pack, each with its label.
            label: addAsLabel(offered, lng),
            value: addAsOptionValue(offered),
          })),
          ...attachOne.map(({ item }, index) => ({
            label: addToItemLabel(item.label, lng),
            value: attachOptionValue(index),
          })),
          ...(attachEach.length > 0
            ? [{ label: addToEachLabel(lng), value: ATTACH_EACH }]
            : []),
          { label: leaveOutLabel(lng), value: ASSET_CONFIRM_CANCEL },
        ],
      });
      setLocalCardActions((current) =>
        new Map(current).set(cardId, {
          kind: "add_asset",
          files: values.files,
          // One file, or more than the items take: every item, by the option.
          attachTo: attachEach.length > 0 ? attachEach : offers.attachTo,
        }),
      );
      return true;
    },
    [appendAssistantPrompt, studyFamilies, workspace.currentDraft],
  );

  // The chat speaks another language than the study: said once per draft in
  // this browser, when its questionnaire is built, as the chat's own line.
  const studyLanguageNoteSaid = useRef(false);
  const contentLanguage = workspace.currentDraft?.contentLanguage;
  const questionnaireBuilt =
    (workspace.currentDraft?.researchQuestions?.length ?? 0) > 0;
  useEffect(() => {
    if (!draftId || loadingDraft || !questionnaireBuilt) return;
    const note = studyLanguageNote(threadLanguage, contentLanguage);
    if (note === null) return;
    const key = STUDY_LANGUAGE_NOTE_KEY + draftId;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // Storage unavailable: said once per mount.
      if (studyLanguageNoteSaid.current) return;
    }
    studyLanguageNoteSaid.current = true;
    appendAssistantPrompt({ content: note });
  }, [
    appendAssistantPrompt,
    contentLanguage,
    draftId,
    loadingDraft,
    questionnaireBuilt,
    threadLanguage,
  ]);

  /**
   * Runs a card the builder added itself (`LocalCardAction`), from the card's
   * own action. Returns true when it ran (so the caller sends nothing to the
   * planner). Honoured only for the card's own options, so typing an option's
   * value into the Other box cannot fire it, and a card answered by typing
   * falls through to an ordinary turn.
   */
  const tryHandleLocalCard = useCallback(
    (
      card: { id: string; options?: Array<{ value: string }> },
      value: string,
      /** Re-opens the card when nothing ran. */
      reopenCard: () => void,
    ): boolean => {
      const action = localCardActions.get(card.id);
      if (!action || !card.options?.some((option) => option.value === value)) {
        return false;
      }
      if (action.kind === "use_new_assets") {
        if (value === NEW_ASSETS_CONFIRM.update) {
          // A turn that did not commit (blocked before it went out, failed or
          // stopped) changed nothing: the card asks again, with its assets.
          void sendGuardedChatTurn({
            command: { kind: "use_new_assets", assetKeys: action.assetKeys },
          }).then((committed) => {
            if (!committed) reopenCard();
          });
        }
        return true;
      }
      if (value === ASSET_CONFIRM_CANCEL) {
        appendAssistantPrompt({ content: leftOutLine(chatLanguage()) });
        return true;
      }
      const family = familyOfAddAsOption(value);
      if (family) {
        void handleChatAssetAdd(family, { files: action.files }, reopenCard);
        return true;
      }
      // The one file the user picked, given more than the items take:
      // the rest stay out, named, and the pick goes on the one item, or is
      // offered on its own to choose among several.
      const givenNames = action.files.map((file) => file.name);
      const picked = givenNames.findIndex(
        (_name, index) => pickGivenOptionValue(index) === value,
      );
      if (picked >= 0) {
        const chosen = { files: [action.files[picked]] };
        const others = givenNames.filter((_name, index) => index !== picked);
        appendAssistantPrompt({
          content: pickLeftOutLine(others, chatLanguage()),
        });
        if (action.attachTo.length === 1) {
          void handleChatAssetAttach(action.attachTo, chosen, reopenCard);
        } else {
          offerMaterial(chosen);
        }
        return true;
      }
      const targets =
        value === ATTACH_EACH
          ? action.attachTo
          : action.attachTo.filter(
              (_offer, index) => attachOptionValue(index) === value,
            );
      if (targets.length > 0) {
        void handleChatAssetAttach(targets, action, reopenCard);
      }
      return true;
    },
    [
      appendAssistantPrompt,
      handleChatAssetAdd,
      handleChatAssetAttach,
      localCardActions,
      offerMaterial,
      sendGuardedChatTurn,
    ],
  );

  useAutosizeTextarea(textareaRef, input, {
    maxHeightPx: CHAT_TEXTAREA_MAX_HEIGHT_PX,
    disabled: chatInputDisabled,
  });

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // The slow warning grows the transcript inside a single send, while
    // `messages` and `sending` hold still — without it here the new row lands
    // below the fold.
  }, [messages, sending, showSlowWarning]);

  // The composer takes focus back when a lock ends only if it had focus as
  // the lock began (B22): a lock never pulls focus from where the user is.
  const composerFocusedRef = useRef(false);
  useEffect(() => {
    if (chatInputDisabled) return;
    if (composerFocusedRef.current) textareaRef.current?.focus();
  }, [chatInputDisabled]);

  const displaySurveyJson = workspace.currentSurveyJson;
  const surveyQuestionCount = workspace.questionCount;

  // Below the split layout, a questionnaire with questions leads and chat is
  // one tap away; the composer stays docked so edits can still be asked for.
  const isNarrowBuilder = useIsNarrowBuilder();
  const [narrowView, setNarrowView] = useState<NarrowBuilderView>("questionnaire");
  const [narrowReplyNote, setNarrowReplyNote] = useState<NarrowReplyNote | null>(null);
  const compactQuestionsOnly = quickEmbed && isDraftReady && surveyQuestionCount > 0 && !clarificationOpen && !busyCause;
  const dockActionsAboveComposer = !compactQuestionsOnly && isEmbeddedStudy && isNarrowBuilder;
  const showNarrowSwitch = !compactQuestionsOnly && isNarrowBuilder && surveyQuestionCount > 0;
  const isNarrowQuestionnaire = showNarrowSwitch && narrowView === "questionnaire";
  const onQuestionsViewport = useCallback((questionsViewport: StudyQuestionsViewport | null) => {
    if (!quickEmbed || !draftId || draftRevision === undefined) return;
    const savedQuestionsVisible = isDraftReady && !loadingDraft && !workspace.isDirty && surveyQuestionCount > 0 &&
      (compactQuestionsOnly || !isNarrowBuilder || isNarrowQuestionnaire);
    postStudyEmbedMessage({source: STUDY_EMBED_SOURCE, type: "quick-questions-displayed", draftToken: draftId, revision: draftRevision, questionsViewport: savedQuestionsVisible ? questionsViewport : null});
  }, [quickEmbed, draftId, draftRevision, isDraftReady, loadingDraft, workspace.isDirty, surveyQuestionCount, compactQuestionsOnly, isNarrowBuilder, isNarrowQuestionnaire]);

  const latestAssistantReply = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === "assistant" && !isLocalMessage(message)) {
        return message;
      }
    }
    return null;
  }, [messages]);
  const seenAssistantReplyIdRef = useRef<string | null>(latestAssistantReply?.id ?? null);
  // Only a reply to a turn sent here is news; a transcript loading is not.
  const awaitingReplyRef = useRef(false);

  useEffect(() => {
    if (sending) {
      awaitingReplyRef.current = true;
    }
  }, [sending]);

  useEffect(() => {
    if (!latestAssistantReply || latestAssistantReply.id === seenAssistantReplyIdRef.current) {
      return;
    }
    seenAssistantReplyIdRef.current = latestAssistantReply.id;
    const isAnswer = awaitingReplyRef.current;
    awaitingReplyRef.current = false;
    if (!isAnswer || !isNarrowQuestionnaire) {
      return;
    }
    const text = firstReplyLine(latestAssistantReply.content ?? "");
    if (text) {
      setNarrowReplyNote({ id: latestAssistantReply.id, text });
    }
  }, [latestAssistantReply, isNarrowQuestionnaire]);

  function showNarrowView(view: NarrowBuilderView) {
    setNarrowView(view);
    if (view === "chat") {
      setNarrowReplyNote(null);
    }
  }
  // The backend owns review eligibility: the minimum question count depends on
  // the ad-instrument roster, so a legal four-question ad study is review-ready
  // and a hardcoded 8 here would lock it out.
  //
  // Unsaved edits keep the button live rather than making the user guess why it
  // greyed out — the verdict in hand describes the last SAVED draft, so it
  // cannot speak for what is on screen. handleAdvance saves first and refuses
  // on the fresh verdict, so an optimistic click costs a round trip, never a
  // bad review.
  const reviewEligible =
    workspace.isDirty || readiness?.isReviewEligible === true;
  // The banner's filter (one owner): an empty shell's count and missing
  // brief are not something to fix, so its line never says a question does.
  const firstBlockingIssue = shownAuthoringIssues(
    shownBlockingIssues,
    workspace.savedDraft,
  )[0];
  const reviewBlockedReason = firstBlockingIssue
    ? formatIssueCopy(firstBlockingIssue, issueCopyContext).text
    : "Review unlocks once the questionnaire is complete.";

  useEffect(() => {
    if (!hasUserSentMessage) {
      // Only count messages the user typed; card-selection echoes are suppressed
      // from the UI and should not trigger the compact input layout.
      // A card's answer is not typed: its record names the message.
      const hasTypedMessage = messages.some(
        (m) => m.role === "user" && !answeredByMessage.has(m.id),
      );
      if (hasTypedMessage) {
        setHasUserSentMessage(true);
      }
    }
  }, [hasUserSentMessage, messages, answeredByMessage]);

  // Each staged file stored once, through the upload route (L8), with its
  // policy checked before the bytes leave (B23); a file already stored by an
  // earlier send of the same words is reused. Null when one cannot be
  // stored: its line says why, and nothing is sent.
  const storeComposerFiles = useCallback(
    async (
      files: Array<File>,
    ): Promise<Array<QuestionnaireTurnAttachmentInput> | null> => {
      const stored = new Map(composerUploads);
      setAssetWritesInFlight((n) => n + 1);
      try {
        for (const file of files) {
          if (stored.has(file)) continue;
          const refusal = assetUploadRefusalLine(file);
          if (refusal) {
            toast.error(refusal);
            return null;
          }
          stored.set(file, await uploadQuestionnaireAssetFileApi(file));
        }
      } catch (error) {
        console.error("Failed to store the composer's files:", error);
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Failed to add your files",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        return null;
      } finally {
        setComposerUploads(stored);
        setAssetWritesInFlight((n) => n - 1);
      }
      return files.map((file) => ({
        fileId: stored.get(file)!.token,
        name: file.name,
      }));
    },
    [composerUploads, draftId, workspace.savedDraft],
  );

  const handleSend = useCallback(async () => {
    if (assetWritesPending || composerLocked || composerSendInFlightRef.current) return;
    composerSendInFlightRef.current = true;
    // Files staged with no words: the material card asks what they become
    // in this study (`offerMaterial`) and takes them. When nothing the study
    // tests can take them, its line asks what they show, and they stay
    // staged for the upload card the reply opens. Words go to the chat as
    // they are (below), and staged files ride with them as the turn's typed
    // input (L8).
    if (!input.trim() && heldComposerFiles.length > 0) {
      if (offerMaterial({ files: heldComposerFiles })) {
        setComposerFiles([]);
      }
      composerSendInFlightRef.current = false;
      return;
    }
    // A message is the user's message and goes to the chat as it is, links and
    // all: the model reads what its words ask for, and the builder never reads
    // them (F15: the detectAssetIntent word regexes are deleted). A link in it
    // is added by the chat's own `add_asset`, as a link in a Build is (family 6).
    // The staged files are stored first, through the upload route every
    // builder file takes, and ride with the words as the turn's typed input
    // (L8): the turn's operations place them. A file that cannot be stored
    // keeps the words and files in the composer, said by its line.
    // One message carries at most the turn's limit (L8, which Nest refuses a
    // turn over): the files past it stay staged for the next send.
    const attachments =
      heldComposerFiles.length > 0
        ? await storeComposerFiles(
            heldComposerFiles.slice(0, QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT),
          )
        : [];
    if (!attachments) {
      composerSendInFlightRef.current = false;
      return;
    }
    // Persist on-screen edits first (the chat edits the PERSISTED draft); if the
    // save fails, surface it and keep the input rather than silently dropping it.
    // Text-collect cards must be valid and saved before a turn that can PLAN.
    // A turn that did not commit keeps its files staged: beside its words
    // handed back, or for its Retry, which resends them.
    void sendGuardedChatTurn(input, { from: "composer", attachments }).then(
      (committed) => {
        if (committed && attachments.length > 0) setComposerTurnEnded(true);
      },
    ).finally(() => {
      composerSendInFlightRef.current = false;
    });
  }, [
    assetWritesPending,
    composerLocked,
    heldComposerFiles,
    input,
    offerMaterial,
    sendGuardedChatTurn,
    storeComposerFiles,
  ]);


  // Images and videos attached, pasted or dropped on the composer are
  // candidate tested assets, but we ASK before creating (a dropped file could
  // be reference material, not a stimulus). They are staged beside the
  // composer's words, which stay so the user can say what the file is
  // (MT-01), and go where the send takes them (`handleSend`). Returns true
  // when files were handled (caller suppresses the default text paste/drop).
  const handleComposerMediaFiles = useCallback(
    (files: Array<File>): boolean => {
      const gate = gateComposerMediaFiles(files);
      if (gate.kind === "ignore") return false;
      // Files a turn does not carry are named, whether or not others came
      // with them (the drop already suppressed the default handling).
      if (gate.policyRefused.length > 0) {
        for (const file of gate.policyRefused) {
          const line = assetUploadRefusalLine(file);
          if (line) {
            toast.error(line);
          }
        }
      }
      if (gate.turnRefused.length > 0) {
        toast.error(
          t("questionnaire.composerFilesUnsupported", {
            files: gate.turnRefused.map((file) => file.name).join(", "),
          }),
        );
      }
      if (gate.media.length === 0) return true;
      if (!draftId) {
        toast.error("Start your questionnaire first, then attach images.");
        return true;
      }
      // Every file is staged: a send carries the turn's limit (`handleSend`)
      // and the files past it stay for the next one, named beside the tiles
      // (H02-AD, H15-CL: files past the limit were dropped).
      setComposerFiles([...heldComposerFiles, ...gate.media]);
      return true;
    },
    [draftId, heldComposerFiles, t],
  );

  const handleComposerPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (handleComposerMediaFiles(files)) {
        event.preventDefault();
      }
    },
    [handleComposerMediaFiles],
  );

  const handleComposerDrop = useCallback(
    (event: React.DragEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      handleComposerMediaFiles(files);
    },
    [handleComposerMediaFiles],
  );

  // The explore confirm card's "Build manually" / "Paste questionnaire"
  // options: the one road that opens an empty workspace. Errors are toasted
  // inside the hook. The opening is in flight until its request settles (the
  // workspace itself shows at once), and the chat's controls stay locked on
  // it: a second click would write over the first, and its failure would put
  // back the pre-click draft.
  const tryHandleExploreConfirm = useCallback(
    async (
      value: string,
      card: { id: string; options: Array<ChatMessageOption> },
    ) => {
      const action = resolveExploreConfirmAction(
        value,
        card.options.map((option) => option.value),
      );
      if (!action) {
        return false;
      }
      setIsOpeningWorkspace(true);
      try {
        // The card is answered in the PATCH that opens the workspace (S4.1).
        const opened = await startManualDraft({
          intent: action,
          answers: interceptAnswer(card, value),
        });
        if (!opened) {
          return true;
        }
        if (action === "paste") {
          setInvitePaste(true);
          textareaRef.current?.focus();
        }
      } catch {
        // Said by the hook.
      } finally {
        setIsOpeningWorkspace(false);
      }
      return true;
    },
    [interceptAnswer, startManualDraft],
  );

  // The card waiting on an answer: the open record's, or a local prompt.
  const lastUnansweredComposerMsg = findLastUnansweredComposerMessage(
    messages,
    isWaitingCard,
  );
  const activeDecisionMsg =
    !sending &&
    lastUnansweredComposerMsg &&
    isUnansweredDecision(lastUnansweredComposerMsg, isWaitingCard)
      ? lastUnansweredComposerMsg
      : null;
  const activeCollectMsg =
    !sending &&
    lastUnansweredComposerMsg &&
    isUnansweredCollect(lastUnansweredComposerMsg, isWaitingCard)
      ? lastUnansweredComposerMsg
      : null;
  /**
   * The user's own Save answers the waiting collect card when it leaves what
   * the card asks for on the saved draft: a plain card, assets of its type
   * the draft did not hold; an attach card, a file on every item (while any
   * item has none the card stays open on those, and its "Continue without
   * adding a file" ends it). The card and the saved assets are read before
   * the Save. Both roads that save for the user, the panel's Save and
   * Review's "Save and continue", run through here, so neither leaves a card
   * waiting for files already saved. A page load, an unsaved add or a
   * declined "Save and continue" sends nothing. Resolves with whether the
   * Save went through, its result, and whether a receipt answered the card.
   */
  const saveAnsweringCard = useCallback(
    async (
      save: (onSaved: (saved: WorkspaceSaved) => void) => Promise<boolean>,
    ): Promise<{
      saved: WorkspaceSaved | undefined;
      ok: boolean;
      answered: boolean;
    }> => {
      const card = activeCollectMsg;
      const savedAssetsBefore = workspace.savedDraft?.testedAssets;
      let saved: WorkspaceSaved | undefined;
      const ok = await save((result) => {
        saved = result;
      });
      if (!ok || !saved?.draft || !card) {
        return { saved, ok, answered: false };
      }
      const after = saved.draft.testedAssets;
      const assetKeys = receiptAssetKeysOf(after, card.collect);
      const before = receiptAssetKeysOf(savedAssetsBefore, card.collect);
      const answers = card.collect.attachTo
        ? assetKeys.length > 0 && attachCollectSatisfied(after, card.collect)
        : !assetKeys.every((key) => before.includes(key));
      if (!answers) {
        return { saved, ok, answered: false };
      }
      const answered = await sendCommand({ kind: "collect_receipt", assetKeys });
      return { saved, ok, answered };
    },
    [activeCollectMsg, sendCommand, workspace.savedDraft],
  );

  const handleAdvance = useCallback(async () => {
    if (didAdvance || assetWritesPending) {
      return;
    }

    // Review compiles the saved draft, so every local buffer is saved first,
    // once the user agrees. The Save's commit compiles in the same request, so
    // its result is the verdict and survey Review acts on, unless the Save
    // answered the waiting card: that turn moved the draft, so Review
    // compiles the draft the server holds now.
    const { saved, ok, answered } = await saveAnsweringCard(saveEditsIfConfirmed);
    if (!ok) {
      return;
    }

    setIsAdvancing(true);

    try {
      // Every Review click compiles the draft the server currently holds, and
      // advances with THAT survey. The cached `workspace.currentSurveyJson` is
      // never the payload and never a reason to skip the compile: a compiled
      // survey does not say which draft produced it, so a reload after a save,
      // or a save whose compile failed, leaves one that silently describes an
      // older questionnaire. Compilation is deterministic and local, so the
      // honest answer is cheaper than tracking which draft the cache belongs to.
      //
      // Saving first is what makes "the persisted draft" the draft on screen;
      // when there is nothing to save, that is already true and we compile alone.
      // Either way the second click after a failure reaches the compiler again,
      // with no unrelated edit or reload needed.
      const compiled = saved?.draft && !answered
        ? {
            surveyJson: saved.surveyJson,
            readiness: saved.readiness ?? null,
            revision: saved.revision ?? null,
            audience: saved.draft.audience ?? null,
          }
        : await compilePersistedDraft();

      // Review's entry applies the fresh backend verdict and says why it
      // stays shut.
      const success = await onSurveyReady({
        ...compiled,
        title: workspace.title,
      });
      if (success) {
        setDidAdvance(true);
      }
    } finally {
      setIsAdvancing(false);
    }
  }, [
    assetWritesPending,
    compilePersistedDraft,
    didAdvance,
    onSurveyReady,
    saveAnsweringCard,
    saveEditsIfConfirmed,
    workspace,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Turn-zero receipt placement (Wave 7, Step 2): OPENING_CHAINS[goalId][0]
  // is always an objective-only `_decision` card, seeded as the transcript's
  // very first message before any user reply exists: the backend contract
  // this whole plan enforces. Nothing that survives to the client per-message
  // (kind/content) marks that card specifically, so chatState carries no
  // anchor for it; the receipt instead anchors on the first user-role message
  // in the WHOLE transcript, which is necessarily the reply to it, whatever
  // form that reply takes (typed text or a DecisionCard button echo).
  const firstUserMessageIndex = useMemo(
    () =>
      // The transcript's own: an attempt that failed before it stays in the
      // chat (`isLocalMessage`), but set no objective.
      messages.findIndex(
        (message) => message.role === "user" && !isLocalMessage(message),
      ),
    [messages],
  );

  // An upload card opens with the files staged in the composer that no turn
  // placed: its intake takes them as it mounts (`initialFiles`), so they
  // leave the composer.
  const activeCollectId = activeCollectMsg?.id ?? null;
  // Latest composer text, read when a card opens without re-running the
  // effect on every keystroke (setInput takes a value, not an updater).
  const latestInputRef = useRef(input);
  latestInputRef.current = input;
  useEffect(() => {
    if (activeCollectId) {
      setComposerFiles((current) => (current.length > 0 ? [] : current));
      const trimmed = latestInputRef.current.trim();
      if (trimmed) {
        const echoesAssistant = messages.some(
          (message) =>
            message.role === "assistant" && message.content?.trim() === trimmed,
        );
        if (echoesAssistant) {
          setInput("");
        }
      }
    }
  }, [activeCollectId, messages, setInput]);
  // Files sent with the composer's words, once their turn has committed
  // (L8). The reply's upload card took the ones it opened for (above).
  // Otherwise every file the turn's user message holds leaves the composer:
  // that record says what became of each, and one no operation placed is
  // offered from it (`unplacedAttachments`, below), after a reload alike.
  // Files staged since the send stay.
  useEffect(() => {
    if (!composerTurnEnded) return;
    setComposerTurnEnded(false);
    if (activeCollectId) return;
    const sent = new Set(
      (lastUserAttachments.attachments ?? []).map((attached) => attached.fileId),
    );
    setComposerFiles((current) =>
      current.filter((file) => !sent.has(composerUploads.get(file)?.token ?? "")),
    );
  }, [activeCollectId, composerTurnEnded, composerUploads, lastUserAttachments]);

  // The files the latest user message carries that no operation placed and
  // no asset holds now (L8): the material card asks what each becomes, as
  // for files sent with no words. Read off the message's record, so a reload
  // asks again rather than losing them; asked once per page.
  const offeredAttachmentsRef = useRef(new Set<string>());
  useEffect(() => {
    // An open server question about the message that carries those files
    // (PLAN's over-cap ask, `requestMessageId`) owns them: the answer's words
    // place them. A question about anything else leaves them offered.
    if (sending || activeCollectId || !lastUserAttachments.id) return;
    if (askedAboutMessageId === lastUserAttachments.id) return;
    const unplaced = lastUserAttachments.attachments.filter(
      (attached) =>
        attached.placedOn === null &&
        attached.publicUrl &&
        !heldFileIds.has(attached.fileId) &&
        !offeredAttachmentsRef.current.has(attached.fileId),
    );
    if (unplaced.length === 0) return;
    for (const attached of unplaced) offeredAttachmentsRef.current.add(attached.fileId);
    // Each is its stored upload: the card that takes it sends that, and
    // nothing is uploaded again.
    const stored = unplaced.map((attached) => {
      const mimeType =
        attached.mimeType ?? (attached.kind === "video" ? "video/mp4" : "image/png");
      const file = new File([], attached.name, { type: mimeType });
      const upload = {
        token: attached.fileId,
        publicUrl: attached.publicUrl!,
        spacesUri: attached.publicUrl!,
        filePath: attached.publicUrl!,
        mimeType,
        originalFilename: attached.name,
        fileSize: 0,
      } as IssuedAssetFile;
      return [file, upload] as const;
    });
    setComposerUploads((current) => new Map([...current, ...stored]));
    offerMaterial({ files: stored.map(([file]) => file) });
  }, [
    activeCollectId,
    askedAboutMessageId,
    heldFileIds,
    lastUserAttachments,
    offerMaterial,
    sending,
  ]);

  // The upload card of a family made from a link (ads, pages) starts with
  // the links the user's message before it holds, read by the one URL parser;
  // the user's "Add to study" is the decision, so nothing is captured before
  // it (lead decision on the QA4 review, option 2: the pre-draft brief that
  // named its ad by link).
  const briefLinksForCard = useMemo(() => {
    if (!activeCollectMsg || activeCollectMsg.collect.attachTo) return [];
    if (
      !isAssetFamily(activeCollectMsg.collect.assetType) ||
      !LINK_MATERIAL_FAMILIES.includes(activeCollectMsg.collect.assetType)
    ) {
      return [];
    }
    const index = messages.findIndex(
      (message) => message.id === activeCollectMsg.id,
    );
    const brief = messages
      .slice(0, index)
      .reverse()
      .find((message) => message.role === "user");
    return brief ? messageLinks(brief.content) : [];
  }, [activeCollectMsg, messages]);

  // What the waiting collect card has collected while its record is open:
  // the saved assets of its type (plan S4.1). A receipt turn that failed or
  // was stopped left them saved, so the intake offers the receipt for them
  // instead of asking for them again.
  const collectedByActiveCard = activeCollectMsg
    ? receiptAssetKeysOf(
        workspace.savedDraft?.testedAssets,
        activeCollectMsg.collect,
      )
    : [];

  /**
   * The builder's Save button. The collect card and the assets panel share
   * one workspace, so assets added in the panel are the card's too: the
   * user's own Save answers the waiting card (`saveAnsweringCard`). Like every
   * Save, it offers the "update the questions?" card by its one rule
   * (`saveEdits`).
   */
  const saveFromPanel = useCallback(async () => {
    await saveAnsweringCard(savePendingBuffers);
  }, [saveAnsweringCard, savePendingBuffers]);

  const handleDecisionSelect = useCallback(
    async (value: string) => {
      if (!activeDecisionMsg) {
        if (busyCause) {
          toast.info(lockReasonFor(busyCause));
        }
        return;
      }
      if (chatInputDisabled) {
        if (busyCause) {
          toast.info(lockReasonFor(busyCause));
        }
        return;
      }
      if (isLocalMessage(activeDecisionMsg)) {
        setLocalCardAnswers((prev) =>
          new Map(prev).set(activeDecisionMsg.id, value),
        );
      }
      setInputOtherExpanded(false);
      setInputOtherText("");
      const reopenCard = () => {
        setLocalCardAnswers((prev) => {
          const reverted = new Map(prev);
          reverted.delete(activeDecisionMsg.id);
          return reverted;
        });
      };
      // A card the builder added itself runs locally — never a planner turn.
      if (tryHandleLocalCard(activeDecisionMsg, value, reopenCard)) {
        return;
      }
      // The topic gate's "No" opens the audience picker rather than drafting
      // against an audience that cannot answer the study.
      const decision = resolveDecisionCardAction(
        value,
        (activeDecisionMsg.options ?? []).map((option) => option.value),
      );
      if (decision.kind === "change_audience") {
        // The gate is answered by the audience write it leads to (S4.1).
        const answers = interceptAnswer(activeDecisionMsg, value);
        if (answers) answerWithNextAudienceChange(answers);
        void editAudience();
        return;
      }
      if (await tryHandleExploreConfirm(value, activeDecisionMsg)) {
        return;
      }
      const optionId = (activeDecisionMsg.options ?? []).find(
        (option) => option.value === value,
      )?.id;
      // Persist unsaved builder edits first so the planner sees the user's latest
      // questions (the server reconcile is governed by the persisted draft, so an
      // unsaved client-added question would otherwise be dropped). Text-collect
      // cards (claims / pains / pricing / concept) must also be valid before a
      // DecisionCard turn that can reach PLAN. A chosen option sends its id
      // with the text (plan S3.3, L16).
      // Words typed on an asset-confirm card (a "both", a "which 3") answer
      // it, and the files it holds go with them as the turn's input (L8): the
      // card lists a closed set, the person's words may say what it cannot.
      const cardAction = localCardActions.get(activeDecisionMsg.id);
      // One message carries at most the turn's limit; the files past it stay
      // staged for the next send, as the composer's own send keeps them.
      const cardFiles =
        cardAction?.kind === "add_asset"
          ? cardAction.files.slice(0, QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT)
          : [];
      const attachments =
        cardFiles.length > 0 ? await storeComposerFiles(cardFiles) : [];
      if (!attachments) {
        reopenCard();
        return;
      }
      if (cardAction?.kind === "add_asset") {
        const rest = cardAction.files.slice(QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT);
        if (rest.length > 0) setComposerFiles(rest);
      }
      void sendGuardedChatTurn(value, {
        optionId,
        ...(attachments.length > 0 ? { attachments } : {}),
        onBlocked: () => {
          reopenCard();
          if (busyCause) {
            toast.info(lockReasonFor(busyCause));
          }
        },
      });
    },
    [
      activeDecisionMsg,
      answerWithNextAudienceChange,
      busyCause,
      chatInputDisabled,
      interceptAnswer,
      localCardActions,
      onEditAudience,
      sendGuardedChatTurn,
      storeComposerFiles,
      tryHandleLocalCard,
      tryHandleExploreConfirm,
    ],
  );

  const actionBar = (
    <CreationFlowStickyActionBar
      className={cn("px-0", dockActionsAboveComposer ? "pt-1 pb-3" : "pt-3 pb-1")}
      start={
        onEditAudience ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => void editAudience()}
            // Its audience write commits at the revision it read, so it
            // waits for the turn or Save in flight.
            disabled={chatInputDisabled}
            data-testid="chat-edit-audience-button"
            className="h-8 rounded-md pl-0 pr-3 text-[13px] font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            {t("questionnaire.editAudience")}
          </Button>
        ) : undefined
      }
      end={
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Wrapper span keeps the tooltip working while the button is
                  disabled (disabled buttons don't emit pointer events). */}
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="cta"
                  data-testid="questionnaire-builder-continue-button"
                  aria-describedby={
                    reviewEligible ? undefined : REVIEW_BLOCKED_REASON_ID
                  }
                  onClick={handleAdvance}
                  disabled={
                    chatInputDisabled || !reviewEligible || isAdvancing
                  }
                  className="h-9 rounded-lg px-4 text-sm font-medium"
                >
                  {isAdvancing ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      {t("questionnaire.reviewing")}
                    </>
                  ) : (
                    t("questionnaire.reviewStudy")
                  )}
                </Button>
                {/* The tooltip's reason, for a screen reader and the
                    keyboard, which never reach a tooltip on a disabled
                    button (B22). */}
                {!reviewEligible ? (
                  <span id={REVIEW_BLOCKED_REASON_ID} className="sr-only">
                    {reviewBlockedReason}
                  </span>
                ) : null}
              </span>
            </TooltipTrigger>
            {!reviewEligible ? (
              <TooltipContent side="top">
                <span
                  className="text-[11px]"
                  data-testid="questionnaire-review-blocked-reason"
                >
                  {reviewBlockedReason}
                </span>
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      }
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-keyboard-focus-ring="">
      {headerSlot ? (
        <div className="flex flex-shrink-0 items-center justify-end gap-2 pb-3">
          {headerSlot}
        </div>
      ) : null}

      {showNarrowSwitch ? (
        <div
          role="tablist"
          aria-label={t("questionnaire.narrowViewLabel")}
          className="mb-3 inline-flex flex-shrink-0 self-start rounded-lg bg-muted p-0.5 lg:hidden"
          data-testid="questionnaire-narrow-view-switch"
        >
          {(["questionnaire", "chat"] as const).map((view) => (
            <button
              key={view}
              type="button"
              role="tab"
              aria-selected={narrowView === view}
              onClick={() => showNarrowView(view)}
              data-testid={`questionnaire-narrow-view-${view}`}
              className={cn(
                "h-7 rounded-md px-3 text-xs font-medium transition-colors",
                narrowView === view
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {view === "questionnaire"
                ? t("questionnaire.narrowViewQuestionnaire")
                : t("questionnaire.narrowViewChat")}
            </button>
          ))}
        </div>
      ) : null}

      <div
        className={cn(
          "flex min-h-0 flex-1 gap-4",
          // The panel leads; the chat column below it is only the composer.
          isNarrowQuestionnaire && "flex-col-reverse gap-0 lg:flex-row lg:gap-4",
        )}
      >
        <div
          className={cn(
            compactQuestionsOnly ? "hidden" : "flex w-full min-w-0 flex-col lg:w-1/3",
            isNarrowQuestionnaire && "flex-shrink-0 lg:flex-shrink",
          )}
          data-testid="questionnaire-chat-column"
        >
          {/* lang attribute improves browser glyph selection and screen-reader
              behaviour when the study language uses a non-Latin script (e.g. ja). */}
          {/* pt/pb keep the active card's glow ring from being clipped by
              the scroll container's overflow at the first/last message. */}
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            lang={language || undefined}
            className={cn(
              "flex-1 min-h-0 space-y-5 overflow-y-auto pb-3 pr-2 pt-3 scroll-smooth",
              isNarrowQuestionnaire && "hidden lg:block",
            )}
          >
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;
              // Everything below is the ORIGINAL per-message render logic,
              // unchanged, just wrapped so the receipt can be appended after
              // whichever node it produces (see firstUserMessageIndex above).
              const node = (() => {
                // Assistant messages with a collect surface render as a
                // CollectCard: its question in the transcript, its intake in
                // the composer (`ComposerCollectIntake`) — checked before
                // options since collect cards never carry options.
                if (message.role === "assistant" && message.collect) {
                  return (
                    <CollectCard
                      key={message.id}
                      message={{ ...message, collect: message.collect }}
                      chainCode={chainCodes.get(message.id)}
                      assetPanelHeading={assetPanelHeading}
                      aiIconSrc={aiIconSrc}
                    />
                  );
                }

                // Assistant messages with options render as a DecisionCard:
                // its question in the transcript. The waiting card's options
                // live in the composer (`handleDecisionSelect`).
                if (
                  message.role === "assistant" &&
                  Array.isArray(message.options) &&
                  message.options.length > 0
                ) {
                  const cardMessage = message as typeof message & {
                    options: NonNullable<typeof message.options>;
                  };
                  return (
                    <DecisionCard
                      key={message.id}
                      message={cardMessage}
                      chainCode={chainCodes.get(message.id)}
                      aiIconSrc={aiIconSrc}
                      isActive={isLastMessage && !sending}
                      answered={answeredCardIds.has(message.id)}
                    />
                  );
                }

                // A DecisionCard pick renders as a normal user bubble (like the user
                // answered in chat); a CollectCard receipt renders its attachments as
                // a user bubble. Everything else falls through to the plain bubble.
                if (message.role === "user") {
                  // Prefer the option label over an opaque stored value: the
                  // record names the option (S4.1); a transcript stored before
                  // the records is looked up by the value it stored.
                  const answeredCard = answeredByMessage.get(message.id);
                  const decisionLabel = answeredCard?.option
                    ? builderOptionLabel(
                        answeredCard.option,
                        chainCodes.get(answeredCard.cardId),
                        chatT(message.language),
                      )
                    : resolveDecisionOptionLabel(messages, index, message.content);
                  if (decisionLabel !== null) {
                    return (
                      <ChatBubble
                        key={message.id}
                        message={{
                          ...message,
                          content: decisionLabel,
                        }}
                        isFirst={index === 0}
                        isLast={isLastMessage}
                        userInitials={userInitials}
                        aiIconSrc={aiIconSrc}
                        assetPanelHeading={assetPanelHeading}
                        whiteLabelEnabled={whiteLabelEnabled}
                      />
                    );
                  }
                  const answeredCollect = answeredByMessage.get(message.id);
                  // A receipt that adds nothing (an attach card continued
                  // without a file, F19) has no files to show: its own words
                  // ("None added") show below. One that collects adds what
                  // the user submitted, from the send: the server names its
                  // assets only at commit.
                  const receiptNamesNothing =
                    message.command?.kind === "collect_receipt" &&
                    receiptItemCount(message.command) === 0;
                  // An attach card's receipt says which item got which file
                  // (F19), read off the saved items.
                  const attachCard = answeredCollect?.collect
                    ? messages.find(
                        (candidate) => candidate.id === answeredCollect.cardId,
                      )?.collect
                    : undefined;
                  const attachLines =
                    attachCard?.attachTo &&
                    message.command?.kind === "collect_receipt"
                      ? attachReceiptLinesOf(
                          attachCard,
                          message.command.assetOperations ?? [],
                        )
                      : [];
                  if (attachLines.length > 0) {
                    return (
                      <ChatBubble
                        key={message.id}
                        message={{
                          ...message,
                          content: attachLines
                            .map((line) => attachReceiptLine(line, message.language))
                            .join("\n"),
                        }}
                        isFirst={index === 0}
                        isLast={isLastMessage}
                        userInitials={userInitials}
                        aiIconSrc={aiIconSrc}
                        assetPanelHeading={assetPanelHeading}
                        whiteLabelEnabled={whiteLabelEnabled}
                      />
                    );
                  }
                  if (
                    answeredCollect?.collect &&
                    !receiptNamesNothing &&
                    message.command?.kind === "collect_receipt"
                  ) {
                    // Instead of the suppressed receipt text, the files the
                    // user sent, as a normal user chat bubble: read off the
                    // receipt's own operations, in the session and after a
                    // reload alike, never the draft's media now.
                    const sent = receiptSentMedia(message.command);
                    if (sent.length > 0) {
                      return (
                        <SentAttachmentsBubble
                          key={message.id}
                          mediaUrls={sent}
                          userInitials={userInitials}
                          isLast={isLastMessage}
                        />
                      );
                    }
                    // Nothing to show (a link with no capture, read at commit):
                    // the receipt's own words ("1 added") show below.
                  }
                }

                return (
                  <ChatBubble
                    key={message.id}
                    message={message}
                    chainCode={chainCodes.get(message.id)}
                    isFirst={index === 0}
                    isLast={isLastMessage}
                    userInitials={userInitials}
                    aiIconSrc={aiIconSrc}
                    assetPanelHeading={assetPanelHeading}
                    whiteLabelEnabled={whiteLabelEnabled}
                  />
                );
              })();

              // The objective a first message set, and what the message's
              // material did: a receipt turn's collect skips (its stored
              // effects) and a paste's committed receipt, so both show after
              // a reload too.
              const skipLines = userMessageReceiptLines(
                {
                  effects:
                    message.command?.kind === "collect_receipt"
                      ? message.effects
                      : undefined,
                  materialReceipt: message.materialReceipt,
                },
                chatT(message.language),
              );
              const objective =
                index === firstUserMessageIndex && studyGoals?.decision
                  ? studyGoals
                  : null;
              // The files the user attached to these words (L8), as stored:
              // shown in the session and after a reload alike.
              const attachedTiles =
                message.role === "user"
                  ? (message.attachments ?? []).flatMap((attached) =>
                      attached.publicUrl
                        ? [
                            {
                              src: attached.publicUrl,
                              mimeType: attached.mimeType,
                              label: attached.name,
                            },
                          ]
                        : [],
                    )
                  : [];
              if (!objective && skipLines.length === 0 && attachedTiles.length === 0) {
                return node;
              }
              return (
                <Fragment key={message.id}>
                  {attachedTiles.length > 0 ? (
                    <SentAttachmentsBubble
                      mediaUrls={attachedTiles}
                      userInitials={userInitials}
                      isLast={isLastMessage}
                    />
                  ) : null}
                  {node}
                  {objective ? (
                    <ObjectiveReceipt
                      decision={objective.decision}
                      source={objective.source}
                      language={message.language}
                    />
                  ) : null}
                  {skipLines.map((content, lineIndex) => (
                    <ChatBubble
                      key={`${message.id}-skip-${lineIndex}`}
                      message={{
                        id: `${message.id}-skip-${lineIndex}`,
                        role: "assistant",
                        language: message.language,
                        content,
                      }}
                      isFirst={false}
                      isLast={false}
                      userInitials={userInitials}
                      aiIconSrc={aiIconSrc}
                      assetPanelHeading={assetPanelHeading}
                      whiteLabelEnabled={whiteLabelEnabled}
                    />
                  ))}
                </Fragment>
              );
            })}

            {sessionError && canRetryLast ? (
              <div className="ml-11 animate-fade-in">
                <button
                  data-testid="chat-retry-last-message"
                  onClick={() => void retryLastAfterEdits()}
                  className="text-xs text-primary underline hover:text-primary/80"
                >
                  {t("questionnaire.retryLast")}
                </button>
              </div>
            ) : null}

            {turnInFlight ? (
              <>
                <div
                  className="flex items-center gap-3 pl-[36px] animate-fade-in"
                  role="status"
                  aria-live="polite"
                  data-testid="chat-preparing-indicator"
                >
                  <TypingDots />
                  {sendUnconfirmed ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="chat-send-unconfirmed"
                    >
                      {t("questionnaire.turn.sendUnconfirmed")}
                    </p>
                  ) : buildProgressLine ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="chat-build-progress"
                    >
                      {buildProgressLine}
                    </p>
                  ) : null}
                </div>
                {showSlowWarning ? (
                  <div className="mt-1 pl-[36px] flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">
                      {t("questionnaire.takingLonger")}
                    </p>
                    <button
                      onClick={cancelRequest}
                      disabled={!canStop}
                      className="text-xs text-destructive underline hover:text-destructive/80 disabled:opacity-50 dark:text-red-400 dark:hover:text-red-400/80"
                    >
                      {t("questionnaire.cancel")}
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            {/* A chat-side write that carries a video runs outside a turn, so
                `sending` is still false while the Save watches it. */}
            {readingCreative ? (
              <div
                className="flex items-center gap-3 pl-[36px] animate-fade-in"
                role="status"
                aria-live="polite"
                data-testid="chat-reading-creative-indicator"
              >
                <TypingDots />
                <Text size="sm" className="text-muted-foreground">
                  {t("questionnaire.readingAd")}
                </Text>
              </div>
            ) : null}
          </div>

          <div
            className={cn("flex-shrink-0 pt-3", isEmbeddedStudy ? "sticky bottom-0 z-20 bg-background pb-1" : "pb-2")}
            data-testid="questionnaire-composer-dock"
          >
            {dockActionsAboveComposer ? actionBar : null}
            {draftArchived ? (
              <div
                className="mb-3 rounded-lg border border-border bg-muted/60 px-4 py-3 text-sm text-foreground"
                role="status"
                data-testid="questionnaire-archived-notice"
              >
                <p>{t("questionnaire.archived.notice")}</p>
                <Link
                  href={ROUTES.tests}
                  className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
                  data-testid="questionnaire-archived-back-to-list"
                >
                  {t("questionnaire.archived.backToList")}
                </Link>
              </div>
            ) : null}
            {isNarrowQuestionnaire && narrowReplyNote ? (
              <div
                className="mb-2 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-foreground lg:hidden"
                role="status"
                data-testid="questionnaire-narrow-reply-note"
              >
                <span className="min-w-0 flex-1 truncate">{narrowReplyNote.text}</span>
                <button
                  type="button"
                  onClick={() => showNarrowView("chat")}
                  className="flex-shrink-0 text-sm font-medium text-primary hover:underline"
                  data-testid="questionnaire-narrow-reply-view-chat"
                >
                  {t("questionnaire.narrowViewChatLink")}
                </button>
                <button
                  type="button"
                  onClick={() => setNarrowReplyNote(null)}
                  aria-label={t("questionnaire.narrowReplyDismiss")}
                  className="flex size-6 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
                  data-testid="questionnaire-narrow-reply-dismiss"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            <div
              className={cn(
                COMPOSER_SURFACE_CLASS,
                composerFlatSurfaceClassName(),
                "flex min-h-[54px] min-w-0 flex-col rounded-3xl py-3 pl-[52px] pr-[52px] focus-within:border-border",
                (activeDecisionMsg || activeCollectMsg) && "px-4",
              )}
              data-testid="questionnaire-composer"
            >
              {activeDecisionMsg ? (
                /* Option buttons replace the textarea when a decision is pending */
                <div className="flex flex-col gap-1.5">
                  {busyCause ? (
                    <p
                      id="chat-decision-busy-reason"
                      className="text-xs text-muted-foreground px-1"
                      role="status"
                    >
                      {lockReasonFor(busyCause)}
                    </p>
                  ) : null}
                  {activeDecisionMsg.options.map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      data-testid={`chat-decision-option-${index}`}
                      onClick={() => void handleDecisionSelect(option.value)}
                      disabled={chatInputDisabled}
                      aria-describedby={
                        busyCause ? "chat-decision-busy-reason" : undefined
                      }
                      className={cn(
                        "flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-sm text-left transition-colors",
                        "border border-border bg-background/60",
                        "hover:bg-accent hover:border-border",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                    >
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {builderOptionMark(index)}
                      </span>
                      <span className="flex-1 text-foreground">
                        {builderOptionLabel(
                          option,
                          chainCodes.get(activeDecisionMsg.id),
                          chatT(activeDecisionMsg.language),
                        )}
                      </span>
                    </button>
                  ))}

                  {!inputOtherExpanded ? (
                    <button
                      type="button"
                      data-testid="chat-decision-other-toggle"
                      onClick={() => setInputOtherExpanded(true)}
                      disabled={composerLocked}
                      className={cn(
                        "flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-sm text-left transition-colors",
                        "border border-border/50 bg-background/40 text-muted-foreground",
                        "hover:bg-accent hover:text-foreground hover:border-border",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                    >
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {builderOptionMark(activeDecisionMsg.options.length)}
                      </span>
                      <span className="italic">
                        {/* Locked free text says what it waits on, as the
                            composer does. */}
                        {composerLockCause
                          ? lockReasonFor(composerLockCause)
                          : t("questionnaire.otherTypeAnswer")}
                      </span>
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/60 p-2">
                      <Textarea
                        autoFocus
                        data-testid="chat-decision-other-field"
                        value={inputOtherText}
                        onChange={(e) => setInputOtherText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            const trimmed = inputOtherText.trim();
                            if (trimmed) handleDecisionSelect(trimmed);
                          }
                        }}
                        placeholder={
                          composerLockCause
                            ? lockReasonFor(composerLockCause)
                            : t("questionnaire.typeAnswer")
                        }
                        disabled={composerLocked}
                        rows={2}
                        className="min-h-0 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setInputOtherExpanded(false);
                            setInputOtherText("");
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          {t("questionnaire.cancel")}
                        </button>
                        <Button
                          type="button"
                          size="sm"
                          variant="cta"
                          data-testid="chat-decision-other-submit"
                          disabled={!inputOtherText.trim() || composerLocked}
                          onClick={() => {
                            const trimmed = inputOtherText.trim();
                            if (trimmed) handleDecisionSelect(trimmed);
                          }}
                          className="h-7 rounded-md px-3 text-xs"
                        >
                          {t("questionnaire.send")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : activeCollectMsg ? (
                /* Asset intake (upload / landing-page URL) lives in the composer,
                   ChatGPT-style — the matching bubble collapses to its prompt. */
                <ComposerCollectIntake
                  key={activeCollectMsg.id}
                  collect={activeCollectMsg.collect}
                  initialFiles={heldComposerFiles}
                  initialLinks={briefLinksForCard}
                  collectedCount={
                    activeCollectMsg.collect.attachTo
                      ? attachItemsWithFileOf(
                          workspace.savedDraft?.testedAssets,
                          activeCollectMsg.collect,
                        )
                      : collectedByActiveCard.length
                  }
                  attachItems={
                    activeCollectMsg.collect.attachTo
                      ? openAttachItemsOf(
                          workspace.savedDraft?.testedAssets,
                          activeCollectMsg.collect,
                        )
                      : undefined
                  }
                  disabled={chatInputDisabled}
                  onCollect={(values) =>
                    handleCollectSubmit(
                      {
                        id: activeCollectMsg.id,
                        collect: activeCollectMsg.collect,
                      },
                      values,
                    )
                  }
                  onStagedChange={setIntakeStaged}
                />
              ) : (
                <>
                  {heldComposerFiles.length > QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT ? (
                    <p
                      data-testid="chat-composer-attach-limit"
                      className="mb-1 text-sm text-twin-chat-fg-muted"
                    >
                      {t("questionnaire.composerFilesNextMessage", {
                        files: heldComposerFiles
                          .slice(QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT)
                          .map((file) => file.name)
                          .join(", "),
                        count: QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT,
                      })}
                    </p>
                  ) : null}
                  {composerFileTiles.length > 0 ? (
                    <div className="mb-2" data-testid="chat-composer-files">
                      <AttachmentTiles
                        items={composerFileTiles}
                        testIdPrefix="chat-composer-file"
                        align="start"
                        onRemoveAtIndex={
                          composerLocked
                            ? undefined
                            : (index) =>
                                setComposerFiles((current) =>
                                  current.filter(
                                    (file) => file !== heldComposerFiles[index],
                                  ),
                                )
                        }
                      />
                    </div>
                  ) : null}
                  <Textarea
                    ref={textareaRef}
                    onFocus={() => {
                      composerFocusedRef.current = true;
                    }}
                    onBlur={(event) => {
                      // Disabling the composer for a lock is not the user
                      // leaving it.
                      if (!event.currentTarget.disabled) {
                        composerFocusedRef.current = false;
                      }
                    }}
                    placeholder={
                      // The composer is locked on the one busy fact or an open
                      // asset card: it says what it waits on, by its cause, in
                      // the line the panel's locked controls say (one owner,
                      // `lockReasonFor`; a Save says "Saving…", never that the
                      // AI is working), rather than inviting a message it
                      // cannot take. Like the rest of the builder, it is in
                      // the interface's language, whatever language the
                      // questionnaire is in (QA4: a Spanish study turned it
                      // Spanish inside an English builder).
                      composerLockCause
                        ? lockReasonFor(composerLockCause)
                        : invitePaste
                          ? t("questionnaire.pasteQuestions")
                          : clarificationOpen
                            ? t("questionnaire.composerFollowUp")
                            : isDraftReady
                              ? t("questionnaire.composerDraftReady")
                              : t("questionnaire.composerFirstTurn")
                    }
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handleComposerPaste}
                    onDrop={handleComposerDrop}
                    onDragOver={(event) => {
                      // Only claim the drag when it carries files, so text DnD is
                      // unaffected.
                      if (
                        Array.from(event.dataTransfer?.types ?? []).includes(
                          "Files",
                        )
                      ) {
                        event.preventDefault();
                      }
                    }}
                    className={cn(
                      // shrink-0, not flex-1: this shell is a column flex with
                      // only a min-height, and flex-1's 0% basis keeps the
                      // field at that leftover height. The autosize height
                      // never applies, so a long message is clipped.
                      "w-full shrink-0 resize-none border-0 bg-transparent p-0 !text-base leading-[1.6] text-twin-chat-fg placeholder:text-twin-chat-fg-muted shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                      "max-h-[120px] min-h-[24px]",
                    )}
                    rows={1}
                    disabled={composerLocked}
                    data-testid="chat-message-input"
                  />
                  {sending ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={cancelRequest}
                            // Stop acts on the current turn's id only: off
                            // until the server names it, and once its stop
                            // is asked for.
                            disabled={!canStop}
                            data-testid="chat-stop-button"
                            className={cn(COMPOSER_ROUND_ACTION_CLASS, "group absolute bottom-2 right-2 size-9 bg-primary text-primary-foreground transition-opacity disabled:cursor-default disabled:opacity-60")}
                            aria-label={t("questionnaire.stop")}
                          >
                            <Loader2
                              className="h-4 w-4 animate-spin group-hover:hidden group-focus-visible:hidden"
                              aria-hidden="true"
                            />
                            <Square
                              className="hidden h-3 w-3 fill-current stroke-none group-hover:block group-focus-visible:block"
                              aria-hidden="true"
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {t("questionnaire.stop")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={
                        (!input.trim() && heldComposerFiles.length === 0) ||
                        composerLocked
                      }
                      data-testid="chat-send-button"
                      className={cn(COMPOSER_ROUND_ACTION_CLASS, "absolute bottom-2 right-2 size-9 bg-primary text-primary-foreground transition-colors disabled:bg-twin-chat-bubble disabled:text-twin-chat-send-fg")}
                      aria-label={t("questionnaire.send")}
                    >
                      {chatInputDisabled ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => composerFileInputRef.current?.click()}
                    disabled={composerLocked}
                    data-testid="chat-attach-button"
                    title={t("questionnaire.attachFiles")}
                    aria-label={t("questionnaire.attachFiles")}
                    className="absolute bottom-2 left-2 flex size-9 items-center justify-center rounded-full text-twin-chat-fg-muted transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                  >
                    <Plus className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <input
                    ref={composerFileInputRef}
                    type="file"
                    accept={COMPOSER_MEDIA_ACCEPT}
                    multiple
                    className="sr-only"
                    data-testid="chat-attach-input"
                    onChange={(event) => {
                      handleComposerMediaFiles(Array.from(event.target.files ?? []));
                      event.target.value = "";
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <div
          className={cn(
            compactQuestionsOnly ? "flex min-h-0 w-full flex-1 overflow-hidden" : "min-h-0 lg:flex lg:w-2/3",
            // overflow-hidden: the panel scrolls inside, the composer below stays put.
            !compactQuestionsOnly && (isNarrowQuestionnaire ? "flex w-full flex-1 overflow-hidden lg:overflow-visible" : "hidden"),
          )}
          data-testid="questionnaire-panel-column"
        >
          <QuestionnaireBuilderPanel
            compact={quickEmbed}
            onQuestionsViewport={quickEmbed ? onQuestionsViewport : undefined}
            draftId={draftId}
            hasUserSentMessage={hasUserSentMessage}
            language={language}
            onLanguageChange={
              onLanguageChange ? handleLanguageSelect : undefined
            }
            studyBrand={studyBrand}
            onStudyBrandSave={hasDraft ? saveStudyBrand : undefined}
            workspace={workspace}
            highlightValues={[]}
            busy={busyCause}
            turnStage={turnStage}
            turnStageTotal={turnStageTotal}
            stopRequested={stopRequested}
            isOpeningWorkspace={isOpeningWorkspace}
            coverageByQuestionId={coverageByQuestionId}
            issuesByQuestionId={indexedIssues.byQuestionId}
            unscopedIssues={indexedIssues.unscoped}
            issuesByAssetId={indexedIssues.byAssetId}
            advisoriesByQuestionId={indexedAdvisories.byQuestionId}
            unscopedAdvisories={indexedAdvisories.unscoped}
            studyGoals={studyGoals}
            onSaveStudyGoals={saveObjectiveAfterEdits}
            onObjectiveEditorChange={onObjectiveEditorChange}
            isSavingStudyGoals={isSavingStudyGoals}
            onSave={saveFromPanel}
          />
        </div>
      </div>

      {!dockActionsAboveComposer ? actionBar : null}

      <Dialog
        open={pendingLanguage !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingLanguage(null);
          }
        }}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="language-translate-confirm"
        >
          {/* What a translation changes, exactly (python-api
              `translation.py`): the respondents' text, in the interface's
              language like the rest of the builder. The tested material is
              the user's and stays as written. */}
          <DialogHeader>
            <DialogTitle>{t("questionnaire.translateTitle")}</DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                {t("questionnaire.translateBody", {
                  language: languageName(dialogLanguage ?? ""),
                })}
              </span>
              <span className="block">
                {t("questionnaire.translateMaterial", {
                  language: languageName(dialogLanguage ?? ""),
                })}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingLanguage(null)}
              data-testid="language-translate-cancel"
            >
              {t("questionnaire.translateKeep", {
                language: languageName(language ?? "en"),
              })}
            </Button>
            <Button
              type="button"
              variant="cta"
              onClick={confirmLanguageTranslate}
              data-testid="language-translate-confirm-action"
            >
              {t("questionnaire.translateConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={saveContinueOpen}
        onOpenChange={(open) => {
          if (!open) settleSaveContinue(false);
        }}
      >
        <DialogContent className="max-w-sm" data-testid="save-and-continue-confirm">
          <DialogHeader>
            {saveHeldBack ? (
              <>
                <DialogTitle>{saveHeldBack.title}</DialogTitle>
                <DialogDescription data-testid="save-and-continue-held-back">
                  {saveHeldBack.description}
                </DialogDescription>
              </>
            ) : (
              <>
                <DialogTitle>{t("questionnaire.saveContinueTitle")}</DialogTitle>
                <DialogDescription>
                  {t("questionnaire.saveContinueBody")}
                </DialogDescription>
              </>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => settleSaveContinue(false)}
              data-testid="save-and-continue-cancel"
            >
              {t("questionnaire.keepEditing")}
            </Button>
            {saveHeldBack ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => settleSaveContinue("discard")}
                data-testid="save-and-continue-discard"
              >
                {t("questionnaire.discardAndContinue")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="cta"
                onClick={() => settleSaveContinue("save")}
                data-testid="save-and-continue-action"
              >
                {t("questionnaire.saveAndContinue")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
